import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaFolder,
  FaFileArchive,
  FaSitemap,
  FaLock,
  FaSyncAlt,
  FaSearch,
  FaDownload,
  FaTimes,
  FaFileAlt,
  FaTrashAlt,
  FaEye,
  FaEdit,
  FaPlus,
  FaPaste,
  FaUpload,
  FaArrowLeft,
  FaHdd,
  FaCopy,
  FaHome,
} from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import { hasPermission, FILE_MANAGER_PERMISSION } from "../utils/permissions";
import useTranslationStructureGuard from "../hooks/useTranslationStructureGuard";
import resolveDownloadPath, { resolveDirectFileReference } from "../utils/downloads";
import { buildForecastFiles, normalizeFinanceAttachment } from "../utils/financeFiles";
import FilePreviewModal from "../components/FilePreviewModal";
import loadCustomFile from "../utils/loadCustomFile";

export const getCompanyDocumentContentPath = (documentId) => {
  const numericId = Number(documentId);
  if (!Number.isInteger(numericId)) return "";
  return `/company/documents/${encodeURIComponent(numericId)}/content`;
};

const normalizeCompanyDocumentForFileManager = (doc, index) => {
  const contentPath = getCompanyDocumentContentPath(doc?.id);
  return {
    id: doc?.id ?? doc?.localId ?? index,
    name: doc?.title || doc?.originalName || "Documento",
    originalName: doc?.originalName || doc?.title || "Documento",
    fileUrl: doc?.fileUrl || doc?.downloadPath || doc?.relativePath || "",
    contentPath,
  };
};

const getCompanyDocumentFileName = (doc, fallback = "documento") => (
  doc?.originalName || doc?.name || doc?.title || fallback
);

const buildHierarchy = (folders) => {
  if (!Array.isArray(folders) || folders.length === 0) {
    return [];
  }

  const categories = new Map();

  folders.forEach((folder) => {
    const categorySlug = folder.categorySlug || folder.categoryName || "sem-categoria";
    const formSlug = folder.formSlug || folder.formName || "sem-processo";

    if (!categories.has(categorySlug)) {
      categories.set(categorySlug, {
        name: folder.categoryName || "Sem categoria",
        forms: new Map(),
      });
    }

    const categoryEntry = categories.get(categorySlug);
    categoryEntry.name = folder.categoryName || categoryEntry.name;

    if (!categoryEntry.forms.has(formSlug)) {
      categoryEntry.forms.set(formSlug, {
        name: folder.formName || "Processo",
        items: [],
      });
    }

    const formEntry = categoryEntry.forms.get(formSlug);
    formEntry.name = folder.formName || formEntry.name;
    formEntry.items.push(folder);
  });

  return Array.from(categories.entries())
    .map(([categorySlug, data]) => ({
      category: data.name,
      categorySlug,
      forms: Array.from(data.forms.entries())
        .map(([formSlug, formData]) => ({
          formName: formData.name,
          formSlug,
          items: formData.items.sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR")),
        }))
        .sort((a, b) => a.formName.localeCompare(b.formName, "pt-BR")),
    }))
    .sort((a, b) => a.category.localeCompare(b.category, "pt-BR"));
};

const createEmptyOptions = () => ({ users: [], sectors: [] });

const normalizeAccessOptions = (rawOptions) => {
  if (!rawOptions || typeof rawOptions !== "object") {
    return createEmptyOptions();
  }

  const normalizeList = (list) => {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.filter((item) => item && typeof item === "object");
  };

  return {
    users: normalizeList(rawOptions.users),
    sectors: normalizeList(rawOptions.sectors),
  };
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "-";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatDateTime = (isoValue) => {
  if (!isoValue) return "-";
  try {
    const date = new Date(isoValue);
    if (Number.isNaN(date.getTime())) return isoValue;
    return date.toLocaleString();
  } catch {
    return isoValue;
  }
};

const getSimpleFileType = (file = {}) => {
  const name = file.name || file.originalName || "";
  const extension = String(name).split(".").pop();
  if (extension && extension !== name) return extension.toLowerCase();
  const rawType = file.type || file.mimeType || "";
  const subtype = String(rawType).split("/").pop();
  return subtype ? subtype.toLowerCase() : "arquivo";
};

const mergeListMapsById = (primary = {}, secondary = {}) => {
  const keys = new Set([...Object.keys(primary || {}), ...Object.keys(secondary || {})]);
  return Array.from(keys).reduce((acc, key) => {
    const seen = new Set();
    acc[key] = [...(primary[key] || []), ...(secondary[key] || [])].filter((item) => {
      if (!item || typeof item !== "object") return false;
      const id = String(item.id || item.relativePath || item.downloadPath || item.name || JSON.stringify(item));
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return acc;
  }, {});
};

const normalizeSearchValue = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const matchesSearch = (source, query) => {
  if (!query) return true;
  return normalizeSearchValue(source).includes(normalizeSearchValue(query));
};

const DEFAULT_STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024;
const FILE_MANAGER_LOAD_TASKS = 9;

const sortByName = (a = {}, b = {}) => String(a.name || a.title || a.fieldName || "").localeCompare(String(b.name || b.title || b.fieldName || ""), "pt-BR", { sensitivity: "base" });


const TranslationSafeIconButton = React.forwardRef(function TranslationSafeIconButton(
  { icon: Icon, label, className = "", disabled, ...props },
  ref,
) {
  const normalizedLabel = typeof label === "string" ? label : "";

  return (
    <button
      {...props}
      ref={ref}
      disabled={disabled}
      className={className}
      data-translation-guard="true"
      data-label-text={normalizedLabel}
    >
      <span
        aria-hidden="true"
        data-structural-node="icon"
        data-structural-order="0"
        className="flex items-center"
      >
        {Icon ? <Icon /> : null}
      </span>
      <span
        data-structural-node="label"
        data-structural-order="1"
        data-default-label={normalizedLabel}
      >
        {normalizedLabel}
      </span>
    </button>
  );
});


const getLegacyFileReference = (file) => file?.downloadPath || file?.relativePath || file?.downloadUrl || file?.url || (!/^(?:data|blob):/i.test(file?.dataUrl || "") ? file?.dataUrl : "") || "";
const hasRegisteredFileReference = (file) => Boolean(file?.id && getLegacyFileReference(file));
export default function FileManager({ refreshKey }) {
  const { token, user } = useAuth();
  const containerRef = useRef(null);
  const duplicateUploadResolverRef = useRef(null);
  const [folders, setFolders] = useState([]);
  const [options, setOptions] = useState(() => createEmptyOptions());
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ completed: 0, total: 1, label: "Preparando gerenciador..." });
  const [auxiliaryLoading, setAuxiliaryLoading] = useState(false);
  const [studentFolderLoading, setStudentFolderLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterTerm, setFilterTerm] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [sectorSearch, setSectorSearch] = useState("");

  const [selectedCategorySlug, setSelectedCategorySlug] = useState("");
  const [selectedFormSlug, setSelectedFormSlug] = useState("");
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderSummary, setFolderSummary] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [downloadingKey, setDownloadingKey] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [deletingKey, setDeletingKey] = useState("");
  const [renamingFile, setRenamingFile] = useState(null);
  const [fileRenameValue, setFileRenameValue] = useState("");
  const [renamingKey, setRenamingKey] = useState("");
  const [deletingCategory, setDeletingCategory] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewerFolders, setViewerFolders] = useState([]);
  const [categoryEditor, setCategoryEditor] = useState(null);
  const [categoryMerge, setCategoryMerge] = useState(null);
  const [formEditor, setFormEditor] = useState(null);
  const [formMerge, setFormMerge] = useState(null);
  const [fieldEditor, setFieldEditor] = useState(null);
  const [structureSaving, setStructureSaving] = useState(false);
  const [structureError, setStructureError] = useState("");
  const [isProcessRootOpen, setIsProcessRootOpen] = useState(false);
  const [activeAuxiliaryFolder, setActiveAuxiliaryFolder] = useState("");
  const [activeStudentFolder, setActiveStudentFolder] = useState(null);
  const [activeEmployeeFolder, setActiveEmployeeFolder] = useState(null);
  const [activeFinanceFolder, setActiveFinanceFolder] = useState(null);
  const [activeAssetFolder, setActiveAssetFolder] = useState(null);
  const [activeCustomFolder, setActiveCustomFolder] = useState(null);
  const [customFolderStack, setCustomFolderStack] = useState([]);
  const [studentRecords, setStudentRecords] = useState([]);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [financeFileFolders, setFinanceFileFolders] = useState([]);
  const [assetRecords, setAssetRecords] = useState([]);
  const [folderClipboard, setFolderClipboard] = useState(null);
  const [fileOperationHistory, setFileOperationHistory] = useState(() => {
    try {
      const stored = window.localStorage.getItem("file-manager-operation-history");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [selectedCustomItems, setSelectedCustomItems] = useState({});
  const [customFolderTrash, setCustomFolderTrash] = useState(() => {
    try {
      const stored = window.localStorage.getItem("file-manager-custom-folder-trash");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [documentSearchResults, setDocumentSearchResults] = useState([]);
  const [documentSearchLoading, setDocumentSearchLoading] = useState(false);
  const [customFolders, setCustomFolders] = useState(() => {
    try {
      const stored = window.localStorage.getItem("file-manager-custom-folders");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [customFolderFiles, setCustomFolderFiles] = useState(() => {
    try {
      const stored = window.localStorage.getItem("file-manager-custom-folder-files");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [customFileTrash, setCustomFileTrash] = useState(() => {
    try {
      const stored = window.localStorage.getItem("file-manager-custom-file-trash");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [storageLimitBytes, setStorageLimitBytes] = useState(DEFAULT_STORAGE_LIMIT_BYTES);
  const [fileManagerStateLoaded, setFileManagerStateLoaded] = useState(false);
  const [serverStorageUsedBytes, setServerStorageUsedBytes] = useState(0);
  const [duplicateUpload, setDuplicateUpload] = useState(null);

  useTranslationStructureGuard(containerRef);

  const canManageAccess = useMemo(
    () => hasPermission(user, FILE_MANAGER_PERMISSION),
    [user],
  );

  const filteredUsers = useMemo(() => {
    const availableUsers = Array.isArray(options?.users) ? options.users : [];
    const term = userSearch.trim();
    if (!term) return availableUsers;
    return availableUsers.filter((userOption) =>
      matchesSearch(`${userOption.name || ""} ${userOption.email || ""}`, term),
    );
  }, [options?.users, userSearch]);

  const filteredSectors = useMemo(() => {
    const availableSectors = Array.isArray(options?.sectors) ? options.sectors : [];
    const term = sectorSearch.trim();
    if (!term) return availableSectors;
    return availableSectors.filter((sectorOption) =>
      matchesSearch(sectorOption.name || "", term),
    );
  }, [options?.sectors, sectorSearch]);

  const filteredUserIds = useMemo(
    () => filteredUsers.map((userOption) => String(userOption.id)),
    [filteredUsers],
  );

  const filteredSectorIds = useMemo(
    () => filteredSectors.map((sectorOption) => String(sectorOption.id)),
    [filteredSectors],
  );

  const refreshStorageUsage = async () => {
    try {
      const usageRes = await api.get("/system-settings/file-manager-storage-usage");
      const usedBytes = Number(usageRes.data?.usedBytes);
      setServerStorageUsedBytes(Number.isFinite(usedBytes) && usedBytes >= 0 ? usedBytes : 0);
    } catch (usageError) {
      console.error("Falha ao carregar uso de armazenamento", usageError);
    }
  };

  const persistFileManagerState = async (nextFolders = customFolders, nextFiles = customFolderFiles) => {
    try {
      const res = await api.put("/system-settings/file-manager-state", {
        customFolders: nextFolders,
        customFolderFiles: nextFiles,
      });
      if (res.data?.customFolders) setCustomFolders(res.data.customFolders);
      if (res.data?.customFolderFiles) setCustomFolderFiles(res.data.customFolderFiles);
      return true;
    } catch (err) {
      console.error("Falha ao salvar estado global do gerenciador de arquivos", err);
      alert("Não foi possível sincronizar esta alteração para todos os usuários.");
      return false;
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    setLoadProgress({ completed: 0, total: FILE_MANAGER_LOAD_TASKS, label: "Carregando pastas principais..." });

    let completedTasks = 0;
    const advanceLoadProgress = (label) => {
      completedTasks += 1;
      setLoadProgress({
        completed: Math.min(completedTasks, FILE_MANAGER_LOAD_TASKS),
        total: FILE_MANAGER_LOAD_TASKS,
        label,
      });
    };

    try {
      const folderRes = await api.get("/document-folders");

      if (!Array.isArray(folderRes.data)) {
        throw new Error("INVALID_DOCUMENT_FOLDERS_RESPONSE");
      }

      setFolders(folderRes.data);
      advanceLoadProgress("Pastas principais carregadas.");
      // A navegação principal já pode ser usada. As bibliotecas pesadas
      // continuam em segundo plano sem manter a tela inteira bloqueada.
      setLoading(false);
      setAuxiliaryLoading(true);

      const loadStorageLimit = async () => {
        try {
          const storageLimitRes = await api.get("/system-settings/file-manager-storage-limit");
          const nextLimit = Number(storageLimitRes.data?.limit);
          setStorageLimitBytes(Number.isFinite(nextLimit) && nextLimit > 0 ? nextLimit : DEFAULT_STORAGE_LIMIT_BYTES);
        } catch (storageLimitError) {
          console.error("Falha ao carregar limite de armazenamento", storageLimitError);
          setStorageLimitBytes(DEFAULT_STORAGE_LIMIT_BYTES);
        } finally {
          advanceLoadProgress("Limite de armazenamento carregado.");
        }
      };

      const loadStorageUsage = async () => {
        await refreshStorageUsage();
        advanceLoadProgress("Uso de armazenamento atualizado.");
      };

      const loadFileManagerState = async () => {
        try {
          const stateRes = await api.get("/system-settings/file-manager-state");
          const serverFolders = stateRes.data?.customFolders || {};
          const serverFiles = stateRes.data?.customFolderFiles || {};
          const mergedFolders = mergeListMapsById(serverFolders, customFolders);
          const mergedFiles = mergeListMapsById(serverFiles, customFolderFiles);
          setCustomFolders(mergedFolders);
          setCustomFolderFiles(mergedFiles);
          setFileManagerStateLoaded(true);
          if (JSON.stringify(mergedFolders) !== JSON.stringify(serverFolders) || JSON.stringify(mergedFiles) !== JSON.stringify(serverFiles)) {
            await persistFileManagerState(mergedFolders, mergedFiles);
          }
        } catch (stateError) {
          console.error("Falha ao carregar estado global do gerenciador de arquivos", stateError);
          setFileManagerStateLoaded(true);
        } finally {
          advanceLoadProgress("Pastas personalizadas sincronizadas.");
        }
      };

      const loadStudentRecords = async () => {
        try {
          const studentRes = await api.get("/student-management/students", { params: { fileIndex: true } });
          const compactStudents = Array.isArray(studentRes.data) ? studentRes.data : [];
          setStudentRecords(compactStudents.map((student) => ({ ...student, _fileDocumentsLoaded: false })));
        } catch (studentError) {
          console.error("Falha ao carregar arquivos de alunos", studentError);
          setStudentRecords([]);
        } finally {
          advanceLoadProgress("Arquivos de alunos carregados.");
        }
      };

      const loadEmployeeRecords = async () => {
        try {
          const employeeRes = await api.get("/human-resources/employees");
          const employees = Array.isArray(employeeRes.data?.data) ? employeeRes.data.data : [];
          setEmployeeRecords(employees);
        } catch (employeeError) {
          console.error("Falha ao carregar arquivos de colaboradores", employeeError);
          setEmployeeRecords([]);
        } finally {
          advanceLoadProgress("Arquivos de colaboradores carregados.");
        }
      };

      const loadFinanceFiles = async () => {
        try {
          const accountRes = await api.get("/finance/accounts");
          const accounts = Array.isArray(accountRes.data?.accounts) ? accountRes.data.accounts : Array.isArray(accountRes.data) ? accountRes.data : [];
          const transactionsByAccount = await Promise.all(accounts.map(async (account) => {
            try {
              const transactionRes = await api.get(`/finance/accounts/${account.id}/transactions`);
              const transactions = Array.isArray(transactionRes.data?.transactions) ? transactionRes.data.transactions : [];
              return { account, transactions };
            } catch {
              return { account, transactions: [] };
            }
          }));
          let accountability = {};
          let forecastExpenses = [];
          try { accountability = JSON.parse(window.localStorage.getItem("financeAccountability") || "{}"); } catch { accountability = {}; }
          try { forecastExpenses = JSON.parse(window.localStorage.getItem("finance_expense_forecast_v1") || "[]"); } catch { forecastExpenses = []; }

          const accountabilityFiles = transactionsByAccount.flatMap(({ account, transactions }) => transactions.flatMap((transaction) => {
            const data = accountability?.[transaction.id] || {};
            const attachments = Array.isArray(data.attachments) ? data.attachments : [];
            return attachments.map((attachment) => normalizeFinanceAttachment(attachment, {
              id: attachment.id || `${transaction.id}-${attachment.name}`,
              name: attachment.name || transaction.documentNumber || transaction.description || "Documento financeiro",
              category: data.documentType || "Prestação de contas",
              accountName: account.name || account.bank || "Conta",
              transactionDescription: transaction.description || "Transação",
            }));
          }));

          const forecastFiles = buildForecastFiles(forecastExpenses);

          setFinanceFileFolders([
            { id: "accountability", title: "Prestação de contas", documents: accountabilityFiles },
            { id: "forecast", title: "Previsão de despesas", documents: forecastFiles },
          ]);
        } catch (financeError) {
          console.error("Falha ao carregar arquivos financeiros", financeError);
          setFinanceFileFolders([]);
        } finally {
          advanceLoadProgress("Arquivos financeiros carregados.");
        }
      };

      const loadAssetAndCompanyFiles = async () => {
        const [assetResult, companyResult] = await Promise.allSettled([
          api.get("/assets/state", { params: { includeAttachments: true } }),
          api.get("/company"),
        ]);

        if (assetResult.status === "fulfilled") {
          const assetState = assetResult.value.data?.data || {};
          setAssetRecords(Array.isArray(assetState.assets) ? assetState.assets : []);
        } else {
          console.error("Falha ao carregar arquivos de patrimônio", assetResult.reason);
          setAssetRecords([]);
        }

        if (companyResult.status === "fulfilled") {
          const documents = Array.isArray(companyResult.value.data?.documents) ? companyResult.value.data.documents : [];
          setCompanyDocuments(documents.map(normalizeCompanyDocumentForFileManager));
        } else {
          console.error("Falha ao carregar documentos cadastrais", companyResult.reason);
          setCompanyDocuments([]);
        }
        advanceLoadProgress("Arquivos de patrimônio e empresa carregados.");
      };

      const loadAccessOptions = async () => {
        if (!canManageAccess) {
          setOptions(createEmptyOptions());
          advanceLoadProgress("Permissões de acesso atualizadas.");
          return;
        }
        try {
          const optionRes = await api.get("/document-folders/access-options");
          setOptions(normalizeAccessOptions(optionRes.data));
        } catch (optionError) {
          console.error("Falha ao carregar opções de acesso", optionError);
          setOptions((prev) => normalizeAccessOptions(prev));
        } finally {
          advanceLoadProgress("Permissões de acesso atualizadas.");
        }
      };

      await Promise.allSettled([
        loadStorageLimit(),
        loadStorageUsage(),
        loadFileManagerState(),
        loadStudentRecords(),
        loadEmployeeRecords(),
        loadFinanceFiles(),
        loadAssetAndCompanyFiles(),
        loadAccessOptions(),
      ]);
      setAuxiliaryLoading(false);
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível carregar as pastas";
      setError(message);
      setAuxiliaryLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, canManageAccess]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(fileSearch.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [fileSearch]);

  useEffect(() => {
    if (!activeFolder) return;
    let cancelled = false;
    setFilesLoading(true);
    setFilesError("");

    const params = {
      q: debouncedSearch || undefined,
      year: yearFilter || undefined,
      month: monthFilter || undefined,
    };

    api
      .get(`/document-folders/${activeFolder.id}/files`, { params })
      .then((res) => {
        if (cancelled) return;
        setFolderFiles(res.data?.files || []);
        setFolderSummary(res.data?.summary || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const message =
          status === 403
            ? "Você não tem permissão para visualizar os anexos desta pasta."
            : err.response?.data?.error || "Não foi possível carregar os anexos";
        setFilesError(message);
        setFolderFiles([]);
        setFolderSummary(null);
      })
      .finally(() => {
        if (!cancelled) {
          setFilesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeFolder, debouncedSearch, yearFilter, monthFilter]);

  useEffect(() => {
    if (!folderSummary) return;
    if (!yearFilter) {
      setMonthFilter("");
      return;
    }
    const months = folderSummary.monthsByYear?.[yearFilter] || [];
    if (!months.some((month) => month.value === monthFilter)) {
      setMonthFilter("");
    }
  }, [folderSummary, yearFilter, monthFilter]);

  useEffect(() => {
    try {
      window.localStorage.setItem("file-manager-custom-folders", JSON.stringify(customFolders));
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [customFolders]);

  useEffect(() => {
    try {
      window.localStorage.setItem("file-manager-custom-folder-files", JSON.stringify(customFolderFiles));
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [customFolderFiles]);

  useEffect(() => {
    if (!fileManagerStateLoaded || !canManageAccess) return undefined;
    const handle = setTimeout(() => {
      api.put("/system-settings/file-manager-state", {
        customFolders,
        customFolderFiles,
      }).catch((err) => {
        console.error("Falha ao salvar estado global do gerenciador de arquivos", err);
        setError(err?.response?.data?.error || "Não foi possível salvar as alterações do gerenciador de arquivos no servidor. Tente novamente antes de sair.");
      });
    }, 400);
    return () => clearTimeout(handle);
  }, [customFolders, customFolderFiles, fileManagerStateLoaded, canManageAccess]);

  useEffect(() => {
    try {
      window.localStorage.setItem("file-manager-custom-file-trash", JSON.stringify(customFileTrash));
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [customFileTrash]);


  useEffect(() => {
    try {
      window.localStorage.setItem("file-manager-operation-history", JSON.stringify(fileOperationHistory));
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [fileOperationHistory]);

  useEffect(() => {
    try {
      window.localStorage.setItem("file-manager-custom-folder-trash", JSON.stringify(customFolderTrash));
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [customFolderTrash]);

  const hierarchy = useMemo(() => buildHierarchy(folders), [folders]);

  const categoryOptions = useMemo(
    () =>
      hierarchy.map((category) => ({
        slug: category.categorySlug,
        name: category.category,
      })),
    [hierarchy],
  );

  const formMergeOptions = useMemo(() => {
    if (!formMerge) return [];
    const category = hierarchy.find((entry) => entry.categorySlug === formMerge.categorySlug);
    return category ? category.forms : [];
  }, [formMerge, hierarchy]);

  const filteredHierarchy = useMemo(() => {
    const term = normalizeSearchValue(filterTerm.trim());

    return hierarchy
      .map((category) => {
        if (!term) return category;
        const filteredForms = category.forms
          .map((form) => {
            const filteredItems = form.items.filter((folder) => {
              const haystack = [
                category.category,
                form.formName,
                folder.fieldName,
                folder.basePath,
              ]
                .join(" ");
              return matchesSearch(haystack, term);
            });
            if (filteredItems.length === 0) return null;
            return { ...form, items: filteredItems };
          })
          .filter(Boolean);

        if (filteredForms.length === 0) return null;
        return { ...category, forms: filteredForms };
      })
      .filter(Boolean);
  }, [hierarchy, filterTerm]);

  useEffect(() => {
    const term = filterTerm.trim();
    if (term.length < 2 || folders.length === 0) {
      setDocumentSearchResults([]);
      setDocumentSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setDocumentSearchLoading(true);
      try {
        const results = await Promise.all(
          folders.map(async (folder) => {
            try {
              const res = await api.get(`/document-folders/${folder.id}/files`, { params: { q: term } });
              const files = res.data?.files || [];
              return files.map((file) => ({ file, folder }));
            } catch {
              return [];
            }
          }),
        );

        if (!cancelled) {
          setDocumentSearchResults(results.flat());
        }
      } finally {
        if (!cancelled) {
          setDocumentSearchLoading(false);
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [filterTerm, folders]);

  useEffect(() => {
    if (filteredHierarchy.length === 0) {
      if (selectedCategorySlug || selectedFormSlug) {
        setSelectedCategorySlug("");
        setSelectedFormSlug("");
      }
      return;
    }

    const categorySlugs = filteredHierarchy.map((category) => category.categorySlug);

    if (selectedCategorySlug && !categorySlugs.includes(selectedCategorySlug)) {
      setSelectedCategorySlug("");
      setSelectedFormSlug("");
      return;
    }

    if (!selectedCategorySlug) {
      return;
    }

    const category = filteredHierarchy.find((item) => item.categorySlug === selectedCategorySlug);
    const formSlugs = (category?.forms || []).map((form) => form.formSlug);

    if (selectedFormSlug && !formSlugs.includes(selectedFormSlug)) {
      setSelectedFormSlug("");
    }
  }, [filteredHierarchy, selectedCategorySlug, selectedFormSlug]);

  const activeCategory = useMemo(
    () => filteredHierarchy.find((category) => category.categorySlug === selectedCategorySlug) || null,
    [filteredHierarchy, selectedCategorySlug],
  );

  const activeProcess = useMemo(() => {
    if (!activeCategory) return null;
    return activeCategory.forms.find((form) => form.formSlug === selectedFormSlug) || null;
  }, [activeCategory, selectedFormSlug]);

  const openAccessModal = (folder) => {
    if (!canManageAccess) return;
    setEditing({
      id: folder.id,
      title: `${folder.categoryName} › ${folder.formName} › ${folder.fieldName}`,
      userIds: folder.allowedUsers?.map((user) => String(user.id)) || [],
      sectorIds: folder.allowedSectors?.map((sector) => String(sector.id)) || [],
    });
    setUserSearch("");
    setSectorSearch("");
  };

  const closeModal = () => {
    setEditing(null);
    setSaving(false);
    setUserSearch("");
    setSectorSearch("");
  };

  const openViewer = (folder, relatedFolders = []) => {
    if (!folder) return;
    setActiveFolder(folder);
    setViewerFolders(relatedFolders.length > 0 ? relatedFolders : [folder]);
    setFolderFiles([]);
    setFolderSummary(null);
    setFilesError("");
    setFileSearch("");
    setYearFilter("");
    setMonthFilter("");
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
    setRenamingFile(null);
    setFileRenameValue("");
    setRenamingKey("");
  };

  const openProcessViewer = (form, { manageAccess = false } = {}) => {
    if (!form?.items?.length) return;
    const sortedFolders = form.items
      .slice()
      .sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR"));
    const [firstFolder] = sortedFolders;
    if (!firstFolder) return;
    if (manageAccess && canManageAccess) {
      closeViewer();
      openAccessModal(firstFolder);
      return;
    }
    openViewer(firstFolder, sortedFolders);
  };

  const changeViewerFolder = (folderId) => {
    if (!folderId || viewerFolders.length === 0) return;
    const nextFolder = viewerFolders.find((item) => String(item.id) === String(folderId));
    if (!nextFolder || nextFolder.id === activeFolder?.id) {
      return;
    }
    setActiveFolder(nextFolder);
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
  };

  const closeViewer = () => {
    setActiveFolder(null);
    setViewerFolders([]);
    setFolderFiles([]);
    setFolderSummary(null);
    setFilesError("");
    setFileSearch("");
    setYearFilter("");
    setMonthFilter("");
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
    setRenamingFile(null);
    setFileRenameValue("");
    setRenamingKey("");
  };

  const toggleEditingSelection = (key, value) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const current = prev[key] || [];
      const exists = current.includes(value);
      const nextValues = exists ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [key]: nextValues };
    });
  };

  const toggleAllEditingSelections = (key, values) => {
    if (!values || values.length === 0) return;
    setEditing((prev) => {
      if (!prev) return prev;
      const current = prev[key] || [];
      const allSelected = values.every((value) => current.includes(value));
      const nextValues = allSelected
        ? current.filter((value) => !values.includes(value))
        : Array.from(new Set([...current, ...values]));
      return { ...prev, [key]: nextValues };
    });
  };

  const handleSaveAccess = async () => {
    if (!editing || !canManageAccess) return;
    setSaving(true);
    try {
      const payload = {
        userIds: editing.userIds.map((id) => Number(id)),
        sectorIds: editing.sectorIds.map((id) => Number(id)),
      };
      const res = await api.put(`/document-folders/${editing.id}/access`, payload);
      const updated = res.data;
      setFolders((prev) => prev.map((folder) => (folder.id === updated.id ? updated : folder)));
      closeModal();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível salvar o acesso";
      alert(message);
      setSaving(false);
    }
  };

  const availableYears = folderSummary?.years || [];
  const availableMonths = useMemo(() => {
    if (!folderSummary || !yearFilter) return [];
    return folderSummary.monthsByYear?.[yearFilter] || [];
  }, [folderSummary, yearFilter]);

  const requestGroups = useMemo(() => folderSummary?.requestGroups || [], [folderSummary?.requestGroups]);

  const fieldFolderGroups = useMemo(() => {
    const byField = new Map();
    requestGroups.forEach((group) => {
      (group.fields || []).forEach((field) => {
        const folderId = field.folder?.id;
        if (!folderId) return;
        if (!byField.has(folderId)) {
          byField.set(folderId, {
            folder: field.folder,
            files: [],
            totalFiles: 0,
            totalMatching: 0,
            isActive: field.isActive,
          });
        }
        const entry = byField.get(folderId);
        entry.totalFiles += field.totalFiles || 0;
        entry.totalMatching += field.totalMatching || 0;
        entry.files.push(...(field.files || []));
      });
    });

    return Array.from(byField.values())
      .map((field) => ({
        ...field,
        files: field.files.sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()),
      }))
      .sort((a, b) => {
        if (a.isActive === b.isActive) {
          return a.folder.fieldName.localeCompare(b.folder.fieldName, "pt-BR");
        }
        return a.isActive ? -1 : 1;
      });
  }, [requestGroups]);

  const shouldSeparateByFieldFolders = viewerFolders.length > 1 && fieldFolderGroups.length > 0;

  useEffect(() => {
    if (!activeFolder) {
      setExpandedGroups({});
      return;
    }

    if (!shouldSeparateByFieldFolders) {
      setExpandedGroups({});
      return;
    }

    setExpandedGroups((prev) => {
      const next = {};
      fieldFolderGroups.forEach((field) => {
        const key = String(field.folder.id);
        const hasPrevious = Object.prototype.hasOwnProperty.call(prev, key);
        next[key] = hasPrevious ? prev[key] : false;
      });
      return next;
    });
  }, [activeFolder, fieldFolderGroups, shouldSeparateByFieldFolders]);

  const toggleGroupExpansion = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownloadFile = async (file) => {
    const directReference = resolveDirectFileReference(file);
    if (directReference) {
      const anchor = document.createElement("a");
      anchor.href = directReference;
      anchor.download = file.name || "arquivo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return;
    }
    if (!getLegacyFileReference(file) && !file?.contentPath) return;
    if (!token) {
      alert("Faça login para baixar este arquivo.");
      return;
    }
    if (downloadingKey === file.relativePath) return;

    setDownloadingKey(file.relativePath);
    try {
      const requestPath = file.contentPath || resolveDownloadPath(getLegacyFileReference(file));
      const response = await api.get(requestPath, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = file.name || "arquivo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erro ao baixar arquivo", err);
      alert("Não foi possível baixar este arquivo.");
    } finally {
      setDownloadingKey("");
    }
  };

  const handleViewFile = (file) => {
    const directReference = resolveDirectFileReference(file);
    if (!directReference && !getLegacyFileReference(file) && !file?.contentPath) return;
    if (!directReference && !token) {
      alert("Faça login para visualizar este arquivo.");
      return;
    }
    setPreviewFile({ kind: "file", file, name: file?.name || "arquivo" });
  };

  const openFileRename = (file) => {
    if (!canManageAccess || !file?.relativePath) return;
    setRenamingFile(file);
    setFileRenameValue(file.name || "");
  };

  const closeFileRename = () => {
    setRenamingFile(null);
    setFileRenameValue("");
    setRenamingKey("");
  };

  const handleRenameFile = async () => {
    if (!getLegacyFileReference(renamingFile) || !canManageAccess) return;
    const name = fileRenameValue.trim();
    if (!name) {
      alert("Informe o novo nome do arquivo.");
      return;
    }
    if (renamingKey === renamingFile.relativePath) return;

    setRenamingKey(renamingFile.relativePath);
    try {
      const requestPath = resolveDownloadPath(getLegacyFileReference(renamingFile));
      const res = await api.put(requestPath, { name });
      const updated = res.data?.file;
      const nextName = updated?.name || name;
      const nextDownloadPath = updated?.downloadPath || renamingFile.downloadPath || getLegacyFileReference(renamingFile);
      const updateItem = (item) =>
        item.relativePath === renamingFile.relativePath
          ? { ...item, name: nextName, downloadPath: nextDownloadPath }
          : item;
      setFolderFiles((prev) => prev.map(updateItem));
      setFolderSummary((prev) => {
        if (!prev?.requestGroups) return prev;
        return {
          ...prev,
          requestGroups: prev.requestGroups.map((group) => ({
            ...group,
            fields: group.fields.map((field) => ({
              ...field,
              files: field.files.map(updateItem),
            })),
          })),
        };
      });
      closeFileRename();
    } catch (err) {
      console.error("Erro ao renomear arquivo", err);
      alert(err.response?.data?.error || "Não foi possível renomear este arquivo.");
      setRenamingKey("");
    }
  };

  const handleDeleteFile = async (file) => {
    if (!getLegacyFileReference(file)) return;
    if (!token) {
      alert("Faça login para excluir este arquivo.");
      return;
    }
    if (!canManageAccess) {
      alert("Você não tem permissão para excluir anexos.");
      return;
    }
    if (deletingKey === file.relativePath) return;

    const confirmed = window.confirm(
      "Tem certeza que deseja mover este arquivo para a lixeira?",
    );
    if (!confirmed) {
      return;
    }

    setDeletingKey(file.relativePath);
    try {
      const requestPath = resolveDownloadPath(getLegacyFileReference(file));
      await api.delete(requestPath);
      setFolderFiles((prev) => prev.filter((item) => item.relativePath !== file.relativePath));
      setFolderSummary((prev) => {
        if (!prev) return prev;
        const totalFiles = Math.max((prev.totalFiles || 0) - 1, 0);
        const totalMatching = Math.max((prev.totalMatching || 0) - 1, 0);
        const requestGroups = (prev.requestGroups || [])
          .map((group) => {
            const fields = (group.fields || [])
              .map((field) => {
                const nextFiles = (field.files || []).filter((item) => item.relativePath !== file.relativePath);
                const removedFromField = nextFiles.length !== (field.files || []).length;
                return {
                  ...field,
                  files: nextFiles,
                  totalFiles: removedFromField ? Math.max((field.totalFiles || 0) - 1, 0) : field.totalFiles,
                  totalMatching: removedFromField
                    ? Math.max((field.totalMatching || 0) - 1, 0)
                    : field.totalMatching,
                };
              })
              .filter((field) => (field.totalFiles || 0) > 0);

            const removedFromGroup = fields.length !== (group.fields || []).length ||
              (group.fields || []).some((field, index) => field.files?.length !== fields[index]?.files?.length);

            return {
              ...group,
              fields,
              totalFiles: removedFromGroup ? Math.max((group.totalFiles || 0) - 1, 0) : group.totalFiles,
              totalMatching: removedFromGroup ? Math.max((group.totalMatching || 0) - 1, 0) : group.totalMatching,
            };
          })
          .filter((group) => (group.totalFiles || 0) > 0 && (group.fields || []).length > 0);

        return { ...prev, totalFiles, totalMatching, requestGroups };
      });
    } catch (err) {
      console.error("Erro ao excluir arquivo", err);
      alert("Não foi possível mover este arquivo para a lixeira.");
    } finally {
      setDeletingKey("");
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!canManageAccess) return;
    const slug = category?.categorySlug || "";
    if (!slug) {
      alert("Não foi possível identificar a categoria selecionada.");
      return;
    }
    if (deletingCategory === slug) return;

    const confirmed = window.confirm(
      `Tem certeza que deseja mover a categoria "${category.category}" para a lixeira? Todos os anexos associados serão removidos.`,
    );
    if (!confirmed) {
      return;
    }

    setDeletingCategory(slug);
    try {
      const res = await api.delete(`/document-folders/category/${encodeURIComponent(slug)}`);
      const message = res.data?.message || "Categoria movida para a lixeira.";
      alert(message);
      await loadData();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível excluir a categoria.";
      alert(message);
    } finally {
      setDeletingCategory("");
    }
  };

  const openCategoryRename = (category) => {
    if (!canManageAccess || !category?.categorySlug) return;
    setStructureError("");
    setStructureSaving(false);
    setCategoryEditor({
      slug: category.categorySlug,
      originalName: category.category,
      name: category.category,
    });
  };

  const openCategoryMerge = (category) => {
    if (!canManageAccess || !category?.categorySlug) return;
    setStructureError("");
    setStructureSaving(false);
    setCategoryMerge({
      slug: category.categorySlug,
      originalName: category.category,
      targetSlug: "",
      name: category.category,
    });
  };

  const closeCategoryEditor = () => {
    setCategoryEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const closeCategoryMerge = () => {
    setCategoryMerge(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitCategoryRename = async () => {
    if (!categoryEditor || !canManageAccess) return;
    const name = categoryEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o nome da categoria.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/category/${encodeURIComponent(categoryEditor.slug)}`, {
        name,
      });
      const message = res.data?.message || "Categoria atualizada com sucesso.";
      const nextSlug = res.data?.slug || categoryEditor.slug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(nextSlug);
      setSelectedFormSlug("");
      closeCategoryEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar a categoria.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const submitCategoryMerge = async () => {
    if (!categoryMerge || !canManageAccess) return;
    const targetSlug = categoryMerge.targetSlug;
    if (!targetSlug) {
      setStructureError("Selecione a categoria de destino.");
      return;
    }
    if (targetSlug === categoryMerge.slug) {
      setStructureError("Escolha uma categoria diferente para mesclar.");
      return;
    }

    const payload = { slug: targetSlug };
    const name = categoryMerge.name?.trim();
    if (name) {
      payload.name = name;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/category/${encodeURIComponent(categoryMerge.slug)}`, payload);
      const message = res.data?.message || "Categorias mescladas com sucesso.";
      const nextSlug = res.data?.slug || targetSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(nextSlug);
      setSelectedFormSlug("");
      closeCategoryMerge();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível mesclar as categorias.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFormRename = (category, form) => {
    if (!canManageAccess || !category?.categorySlug || !form?.formSlug) return;
    setStructureError("");
    setStructureSaving(false);
    setFormEditor({
      categorySlug: category.categorySlug,
      formSlug: form.formSlug,
      originalName: form.formName,
      name: form.formName,
    });
  };

  const closeFormEditor = () => {
    setFormEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFormRename = async () => {
    if (!formEditor || !canManageAccess) return;
    const name = formEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o novo nome do processo.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const endpoint = `/document-folders/category/${encodeURIComponent(formEditor.categorySlug)}/forms/${encodeURIComponent(
        formEditor.formSlug,
      )}`;
      const res = await api.put(endpoint, { name });
      const message = res.data?.message || "Processo atualizado com sucesso.";
      const nextSlug = res.data?.form?.slug || formEditor.formSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(formEditor.categorySlug);
      setSelectedFormSlug(nextSlug);
      closeFormEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar o processo.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFormMerge = (category, form) => {
    if (!canManageAccess || !category?.categorySlug || !form?.formSlug) return;
    setStructureError("");
    setStructureSaving(false);
    setFormMerge({
      categorySlug: category.categorySlug,
      formSlug: form.formSlug,
      originalName: form.formName,
      targetSlug: "",
      name: form.formName,
    });
  };

  const closeFormMerge = () => {
    setFormMerge(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFormMerge = async () => {
    if (!formMerge || !canManageAccess) return;
    const targetSlug = formMerge.targetSlug;
    if (!targetSlug) {
      setStructureError("Selecione o processo de destino.");
      return;
    }
    if (targetSlug === formMerge.formSlug) {
      setStructureError("Escolha um processo diferente para mesclar.");
      return;
    }

    const payload = { slug: targetSlug };
    const name = formMerge.name?.trim();
    if (name) {
      payload.name = name;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const endpoint = `/document-folders/category/${encodeURIComponent(formMerge.categorySlug)}/forms/${encodeURIComponent(
        formMerge.formSlug,
      )}`;
      const res = await api.put(endpoint, payload);
      const message = res.data?.message || "Processos mesclados com sucesso.";
      const nextSlug = res.data?.form?.slug || targetSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(formMerge.categorySlug);
      setSelectedFormSlug(nextSlug);
      closeFormMerge();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível mesclar os processos.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFieldRename = (folder) => {
    if (!canManageAccess || !folder?.id) return;
    setStructureError("");
    setStructureSaving(false);
    setFieldEditor({
      id: folder.id,
      originalName: folder.fieldName,
      name: folder.fieldName,
    });
  };

  const closeFieldEditor = () => {
    setFieldEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFieldRename = async () => {
    if (!fieldEditor || !canManageAccess) return;
    const name = fieldEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o novo nome do campo.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/${fieldEditor.id}/names`, { fieldName: name });
      const updated = res.data;
      setFolders((prev) => prev.map((folder) => (folder.id === updated.id ? updated : folder)));
      setViewerFolders((prev) =>
        prev.map((folder) => (folder.id === updated.id ? { ...folder, ...updated } : folder)),
      );
      setActiveFolder((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      alert("Campo atualizado com sucesso.");
      closeFieldEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar o campo.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const getCustomFolderChildKey = (key, folder) => `${key}/custom:${folder.id}`;

  const getCurrentCustomFolderKey = () => {
    if (activeCustomFolder) return getCustomFolderChildKey(activeCustomFolder.key, activeCustomFolder.folder);
    if (activeAuxiliaryFolder) return activeAuxiliaryFolder;
    if (isProcessRootOpen && !selectedCategorySlug) return "process-root";
    if (!selectedCategorySlug) return "root";
    if (!selectedFormSlug) return `category:${selectedCategorySlug}`;
    return `process:${selectedCategorySlug}:${selectedFormSlug}`;
  };

  const collectCustomFolderDescendantKeys = (key, folder) => {
    const childKey = getCustomFolderChildKey(key, folder);
    return [
      childKey,
      ...(customFolders[childKey] || []).flatMap((childFolder) => collectCustomFolderDescendantKeys(childKey, childFolder)),
    ];
  };

  const auxiliaryFolders = {
    students: { title: "Arquivos dos Alunos", type: "Biblioteca", size: `${studentRecords.length + (customFolders.students || []).length} pastas` },
    employees: { title: "Arquivos dos Colaboradores", type: "Recursos Humanos", size: `${employeeRecords.filter((employee) => (employee.employeeFiles || []).some((file) => file?.name || file?.dataUrl)).length + (customFolders.employees || []).length} pastas` },
    finance: { title: "Arquivos do Financeiro", type: "Financeiro", size: `${financeFileFolders.length + (customFolders.finance || []).length} pastas` },
    assets: { title: "Arquivos do Patrimônio", type: "Patrimônio", size: `${assetRecords.length + (customFolders.assets || []).length} ativos` },
    registration: { title: "Arquivos dos Dados Cadastrais", type: "Biblioteca", size: `${companyDocuments.length + (customFolders.registration || []).length} itens` },
  };



  const studentFileFolders = useMemo(() => {
    return studentRecords
      .map((student) => {
        const loaded = Boolean(student._fileDocumentsLoaded);
        const documents = loaded && Array.isArray(student.files?.documents) ? student.files.documents : null;
        return {
          id: student.id,
          title: `${student.registrationNumber || "Sem matrícula"} - ${student.fullName || "Aluno"}`,
          student,
          documents,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  }, [studentRecords]);

  const openStudentFileFolder = async (folder) => {
    if (!folder?.id) return;
    if (Array.isArray(folder.documents)) {
      setActiveStudentFolder(folder);
      return;
    }
    setActiveStudentFolder({ ...folder, documents: [] });
    setStudentFolderLoading(true);
    try {
      const response = await api.get(`/student-management/students/${folder.id}`);
      const detailed = response.data || {};
      const documents = Array.isArray(detailed.files?.documents) ? detailed.files.documents : [];
      const loadedStudent = { ...folder.student, ...detailed, _fileDocumentsLoaded: true };
      setStudentRecords((current) => current.map((student) => Number(student.id) === Number(folder.id) ? loadedStudent : student));
      setActiveStudentFolder({ ...folder, student: loadedStudent, documents });
    } catch (studentError) {
      console.error("Falha ao carregar documentos do aluno", studentError);
      setActiveStudentFolder({ ...folder, documents: [], loadError: studentError?.response?.data?.error || "Não foi possível carregar os documentos deste aluno." });
    } finally {
      setStudentFolderLoading(false);
    }
  };


  const employeeFileFolders = useMemo(() => {
    return employeeRecords
      .map((employee) => {
        const documents = Array.isArray(employee.employeeFiles) ? employee.employeeFiles.filter((file) => file?.name || file?.dataUrl) : [];
        return {
          id: employee.id,
          title: employee.fullName || employee.name || `Colaborador ${employee.id}`,
          employee,
          documents,
        };
      })
      .filter((entry) => entry.documents.length > 0)
      .sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  }, [employeeRecords]);

  const assetFileFolders = useMemo(() => {
    return assetRecords
      .map((asset) => ({
        id: asset.id,
        title: `${asset.tag || asset.serialNumber || "Sem etiqueta"} - ${asset.name || "Ativo"}`,
        asset,
        documents: Array.isArray(asset.attachments) ? asset.attachments : [],
      }))
      .sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  }, [assetRecords]);

  const currentCustomFolders = [...(customFolders[getCurrentCustomFolderKey()] || [])].sort(sortByName);

  const customFolderStorageBytes = useMemo(() =>
    Object.values(customFolderFiles).flat().reduce((total, file) => total + (Number(file?.size) || 0), 0),
    [customFolderFiles],
  );

  const visibleFolderStorageBytes = useMemo(() =>
    folderFiles.reduce((total, file) => total + (Number(file?.size) || 0), 0),
    [folderFiles],
  );

  const usedStorageBytes = Math.max(serverStorageUsedBytes, customFolderStorageBytes + visibleFolderStorageBytes);
  const storagePercent = storageLimitBytes > 0 ? Math.min((usedStorageBytes / storageLimitBytes) * 100, 100) : 0;
  const isMasterUser = Boolean(user?.isMaster);

  const handleChangeStorageLimit = async () => {
    if (!isMasterUser) return;
    const currentGb = storageLimitBytes / (1024 * 1024 * 1024);
    const value = window.prompt("Limite de armazenamento em GB", currentGb.toFixed(2));
    if (value === null) return;
    const parsed = Number(String(value).replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert("Informe um limite válido em GB.");
      return;
    }
    try {
      const nextLimit = Math.trunc(parsed * 1024 * 1024 * 1024);
      const res = await api.put("/system-settings/file-manager-storage-limit", { limit: nextLimit });
      setStorageLimitBytes(Number(res.data?.limit) || nextLimit);
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível alterar o limite de armazenamento.");
    }
  };

  const registerFileOperation = (operation) => {
    setFileOperationHistory((prev) => [
      { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, at: new Date().toISOString(), ...operation },
      ...prev,
    ].slice(0, 200));
  };

  const createCustomFolderAtKey = (key, name) => {
    if (!canManageAccess) return false;
    const trimmed = name?.trim();
    if (!trimmed) return false;

    const existing = customFolders[key] || [];
    if (existing.some((folder) => folder.name.toLowerCase() === trimmed.toLowerCase())) {
      alert("Já existe uma pasta com esse nome neste nível.");
      return false;
    }

    const folder = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: trimmed,
      createdAt: new Date().toISOString(),
    };
    const nextFolders = { ...customFolders, [key]: [...existing, folder] };
    setCustomFolders(nextFolders);
    persistFileManagerState(nextFolders, customFolderFiles);
    registerFileOperation({ action: "create-folder", key, name: trimmed });
    return true;
  };

  const handleCreateCustomFolder = (targetKey = getCurrentCustomFolderKey()) => {
    const name = window.prompt("Nome da nova pasta");
    createCustomFolderAtKey(targetKey, name);
  };

  const resetCustomFolderNavigation = () => {
    setActiveCustomFolder(null);
    setCustomFolderStack([]);
  };

  const openCustomFolder = (key, folder) => {
    const nextEntry = { key, folder };
    const parentKey = activeCustomFolder ? getCustomFolderChildKey(activeCustomFolder.key, activeCustomFolder.folder) : null;
    const shouldAppend = parentKey === key && customFolderStack.length > 0;
    const nextStack = shouldAppend ? [...customFolderStack, nextEntry] : [nextEntry];
    setCustomFolderStack(nextStack);
    setActiveCustomFolder(nextEntry);
  };

  const closeCustomFolder = () => {
    if (customFolderStack.length <= 1) {
      resetCustomFolderNavigation();
      return;
    }
    const nextStack = customFolderStack.slice(0, -1);
    setCustomFolderStack(nextStack);
    setActiveCustomFolder(nextStack[nextStack.length - 1] || null);
  };

  const goToFileManagerHome = () => {
    resetCustomFolderNavigation();
    setIsProcessRootOpen(false);
    setActiveAuxiliaryFolder("");
    setActiveStudentFolder(null);
    setSelectedCategorySlug("");
    setSelectedFormSlug("");
  };

  const uploadFileToServer = async (file, desiredFileName = "") => {
    const body = new FormData();
    body.append("file", file);
    if (desiredFileName) {
      body.append("generatedFileName", desiredFileName);
    }
    const res = await api.post("/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: res.data?.name || desiredFileName || file.name,
      size: file.size,
      type: file.type || "Arquivo",
      uploadedAt: new Date().toISOString(),
      downloadPath: res.data?.downloadPath || "",
      relativePath: res.data?.relativePath || "",
    };
  };

  const askDuplicateUploadAction = (fileName) => new Promise((resolve) => {
    duplicateUploadResolverRef.current = resolve;
    setDuplicateUpload({ fileName });
  });

  const resolveDuplicateUploadAction = (action) => {
    if (duplicateUploadResolverRef.current) {
      duplicateUploadResolverRef.current(action);
      duplicateUploadResolverRef.current = null;
    }
    setDuplicateUpload(null);
  };

  const getCustomFileContentPath = (file) => {
    if (!hasRegisteredFileReference(file)) return "";
    return `/system-settings/file-manager-files/${encodeURIComponent(file.id)}/content`;
  };

  const moveCustomFileToSystemTrash = async (file, targetKey) => {
    const contentPath = getCustomFileContentPath(file);
    if (contentPath) {
      await api.delete(`/system-settings/file-manager-files/${encodeURIComponent(file.id)}`);
      return;
    }
    if (file.downloadPath) {
      await api.delete(resolveDownloadPath(getLegacyFileReference(file)));
      return;
    }
    if (file.dataUrl) {
      const response = await fetch(file.dataUrl);
      const blob = await response.blob();
      const legacyFile = new File([blob], file.name || "arquivo", { type: file.type || blob.type || "application/octet-stream" });
      const uploadedLegacy = await uploadFileToServer(legacyFile, file.name || "arquivo");
      if (uploadedLegacy.downloadPath) {
        await api.delete(resolveDownloadPath(uploadedLegacy.downloadPath));
        return;
      }
    }
    setCustomFileTrash((prev) => [
      { ...file, originalKey: targetKey, deletedAt: new Date().toISOString() },
      ...prev,
    ].slice(0, 500));
  };

  const handleUploadCustomFolderFiles = async (event) => {
    if (!canManageAccess) return false;
    const files = Array.from(event.target.files || []);
    if (!activeCustomFolder || files.length === 0) return;

    const totalSelectedSize = files.reduce((total, file) => total + (Number(file.size) || 0), 0);
    const remainingBytes = Math.max(storageLimitBytes - usedStorageBytes, 0);
    if (totalSelectedSize > remainingBytes) {
      alert(`Não é possível enviar estes arquivos. Espaço disponível: ${formatBytes(remainingBytes)}.`);
      event.target.value = "";
      return;
    }

    const storageAfterUpload = usedStorageBytes + totalSelectedSize;
    if (storageLimitBytes > 0 && storageAfterUpload >= storageLimitBytes * 0.8) {
      alert(`Atenção: após este envio restarão apenas ${formatBytes(storageLimitBytes - storageAfterUpload)} do limite de armazenamento.`);
    }

    const targetKey = getCustomFolderChildKey(activeCustomFolder.key, activeCustomFolder.folder);
    try {
      const currentFiles = [...(customFolderFiles[targetKey] || [])];
      const uploadedFiles = [];

      for (const file of files) {
        let desiredName = file.name;
        const duplicated = currentFiles.find((item) => item.name?.toLowerCase() === desiredName.toLowerCase());
        if (duplicated) {
          const duplicateAction = await askDuplicateUploadAction(desiredName);
          if (duplicateAction === "cancel") continue;
          if (duplicateAction === "replace") {
            await moveCustomFileToSystemTrash(duplicated, targetKey);
            const index = currentFiles.findIndex((item) => item.id === duplicated.id);
            if (index >= 0) currentFiles.splice(index, 1);
          } else {
            const renamed = window.prompt("Informe o novo nome do arquivo", desiredName);
            desiredName = renamed?.trim();
            if (!desiredName) continue;
            if (currentFiles.some((item) => item.name?.toLowerCase() === desiredName.toLowerCase())) {
              alert("Já existe um arquivo com esse nome nesta pasta.");
              continue;
            }
          }
        }
        const uploaded = await uploadFileToServer(file, desiredName);
        uploadedFiles.push(uploaded);
        currentFiles.push(uploaded);
      }

      const nextFiles = {
        ...customFolderFiles,
        [targetKey]: currentFiles,
      };
      setCustomFolderFiles(nextFiles);
      await persistFileManagerState(customFolders, nextFiles);
      await refreshStorageUsage();
      registerFileOperation({ action: "upload-files", key: targetKey, count: uploadedFiles.length });
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível enviar um ou mais arquivos.");
    } finally {
      event.target.value = "";
    }
  };

  const getBlobForCustomFolderFile = (file, options = {}) =>
    loadCustomFile(file, getCustomFileContentPath(file), options);

  const handleViewCustomFolderFile = (file) => {
    if (!getLegacyFileReference(file) && !file?.dataUrl) return;
    setPreviewFile({ kind: "custom", file, name: file?.name || "arquivo" });
  };

  const handleDownloadCustomFolderFile = async (file) => {
    if (!getLegacyFileReference(file) && !file?.dataUrl) return;
    try {
      const blob = await getBlobForCustomFolderFile(file);
      if (!blob) return;
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = file.name || "arquivo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erro ao baixar arquivo", err);
      alert("Não foi possível baixar este arquivo.");
    }
  };

  const handleRenameCustomFolderFile = (file) => {
    if (!canManageAccess) return false;
    const targetKey = getCurrentCustomFolderKey();
    const name = window.prompt("Novo nome do arquivo", file.name);
    const trimmed = name?.trim();
    if (!trimmed || trimmed === file.name) return;

    const nextFiles = {
      ...customFolderFiles,
      [targetKey]: (customFolderFiles[targetKey] || []).map((item) =>
        item.id === file.id ? { ...item, name: trimmed, updatedAt: new Date().toISOString() } : item,
      ),
    };
    setCustomFolderFiles(nextFiles);
    persistFileManagerState(customFolders, nextFiles);
    registerFileOperation({ action: "rename-file", key: targetKey, from: file.name, to: trimmed });
  };

  const handleDeleteCustomFolderFile = async (file, options = {}) => {
    if (!canManageAccess) return false;
    const targetKey = options.targetKey || getCurrentCustomFolderKey();
    const confirmed = options.skipConfirm || window.confirm(`Tem certeza que deseja mover o arquivo "${file.name}" para a lixeira?`);
    if (!confirmed) return false;

    try {
      await moveCustomFileToSystemTrash(file, targetKey);
      const nextFiles = {
        ...customFolderFiles,
        [targetKey]: (customFolderFiles[targetKey] || []).filter((item) => item.id !== file.id),
      };
      setCustomFolderFiles(nextFiles);
      await persistFileManagerState(customFolders, nextFiles);
      await refreshStorageUsage();
      registerFileOperation({ action: "trash-file", key: targetKey, name: file.name });
      return true;
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível mover este arquivo para a lixeira.");
      return false;
    }
  };

  const fetchExternalFileBlob = async (doc, options = {}) => {
    if (!token) return null;
    const requestPath = getCompanyDocumentContentPath(doc?.id);
    if (!requestPath) {
      alert("Este documento dos Dados Cadastrais não possui um identificador válido para visualização ou download.");
      return null;
    }
    const response = await api.get(requestPath, { responseType: "blob", signal: options.signal });
    return response.data;
  };

  const handleViewExternalFile = (doc) => {
    if (!getCompanyDocumentContentPath(doc?.id)) {
      alert("Este documento dos Dados Cadastrais não possui um identificador válido para visualização.");
      return;
    }
    setPreviewFile({
      kind: "company-document",
      document: doc,
      name: getCompanyDocumentFileName(doc, "arquivo"),
    });
  };

  const handleDownloadExternalFile = async (doc) => {
    try {
      const blob = await fetchExternalFileBlob(doc);
      if (!blob) return;
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = getCompanyDocumentFileName(doc);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erro ao baixar arquivo", err);
      alert("Não foi possível baixar este arquivo.");
    }
  };

  const handleRenameCustomFolder = (key, folder) => {
    if (!canManageAccess) return false;
    const name = window.prompt("Novo nome da pasta", folder.name);
    const trimmed = name?.trim();
    if (!trimmed || trimmed === folder.name) return;

    const existing = customFolders[key] || [];
    if (existing.some((item) => item.id !== folder.id && item.name.toLowerCase() === trimmed.toLowerCase())) {
      alert("Já existe uma pasta com esse nome neste nível.");
      return;
    }
    const nextFolders = {
      ...customFolders,
      [key]: existing.map((item) => (item.id === folder.id ? { ...item, name: trimmed, updatedAt: new Date().toISOString() } : item)),
    };
    setCustomFolders(nextFolders);
    persistFileManagerState(nextFolders, customFolderFiles);
    registerFileOperation({ action: "rename-folder", key, from: folder.name, to: trimmed });
  };

  const handleCopyCustomFolder = (key, folder) => {
    if (!canManageAccess) return false;
    setFolderClipboard({ key, folder });
    registerFileOperation({ action: "copy-folder", key, name: folder.name });
  };

  const handlePasteCustomFolder = (targetKey = getCurrentCustomFolderKey()) => {
    if (!canManageAccess) return false;
    if (!folderClipboard?.folder) return;
    const name = `${folderClipboard.folder.name} - cópia`;
    createCustomFolderAtKey(targetKey, name);
    registerFileOperation({ action: "paste-folder", from: folderClipboard.key, to: targetKey, name });
  };

  const handleDeleteCustomFolder = async (key, folder, options = {}) => {
    if (!canManageAccess) return false;
    const confirmed = options.skipConfirm || window.confirm(`Tem certeza que deseja mover a pasta "${folder.name}" para a lixeira? Todos os arquivos dentro dela também serão movidos.`);
    if (!confirmed) return false;

    const descendantKeys = collectCustomFolderDescendantKeys(key, folder);
    const deletedAt = new Date().toISOString();
    const folderSnapshot = {
      ...folder,
      originalKey: key,
      deletedAt,
      foldersByKey: Object.fromEntries(
        descendantKeys.map((folderKey) => [folderKey, customFolders[folderKey] || []]),
      ),
      filesByKey: Object.fromEntries(
        descendantKeys.map((folderKey) => [folderKey, customFolderFiles[folderKey] || []]),
      ),
    };

    try {
      const nextFolders = Object.fromEntries(
        Object.entries({
          ...customFolders,
          [key]: (customFolders[key] || []).filter((item) => item.id !== folder.id),
        }).filter(([folderKey]) => !descendantKeys.includes(folderKey)),
      );
      const nextFiles = Object.fromEntries(
        Object.entries(customFolderFiles).filter(([folderKey]) => !descendantKeys.includes(folderKey)),
      );

      setCustomFolders(nextFolders);
      setCustomFolderFiles(nextFiles);
      await persistFileManagerState(nextFolders, nextFiles);
      setCustomFolderTrash((prev) => [folderSnapshot, ...prev]);
      registerFileOperation({ action: "delete-folder", key, name: folder.name });
      return true;
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível mover esta pasta para a lixeira.");
      return false;
    }
  };


  const customSelectionId = (type, id) => `${type}:${id}`;

  const getSelectedCustomIds = (key) => selectedCustomItems[key] || [];

  const isCustomItemSelected = (key, type, id) => getSelectedCustomIds(key).includes(customSelectionId(type, id));

  const toggleCustomItemSelection = (key, type, id) => {
    const selectionId = customSelectionId(type, id);
    setSelectedCustomItems((prev) => {
      const current = prev[key] || [];
      const next = current.includes(selectionId)
        ? current.filter((item) => item !== selectionId)
        : [...current, selectionId];
      return { ...prev, [key]: next };
    });
  };

  const clearCustomSelection = (key) => {
    setSelectedCustomItems((prev) => ({ ...prev, [key]: [] }));
  };

  const getSelectedCustomItems = (key) => {
    const selectedIds = getSelectedCustomIds(key);
    return {
      folders: (customFolders[key] || []).filter((folder) => selectedIds.includes(customSelectionId("folder", folder.id))),
      files: (customFolderFiles[key] || []).filter((file) => selectedIds.includes(customSelectionId("file", file.id))),
    };
  };

  const collectCustomFolderFiles = (key, folder) => {
    const childKey = getCustomFolderChildKey(key, folder);
    return [
      ...(customFolderFiles[childKey] || []),
      ...(customFolders[childKey] || []).flatMap((childFolder) => collectCustomFolderFiles(childKey, childFolder)),
    ];
  };

  const downloadCustomFile = async (file) => {
    if (!getLegacyFileReference(file) && !file?.dataUrl) return false;
    try {
      const blob = await getBlobForCustomFolderFile(file);
      const href = window.URL.createObjectURL(blob);
      const shouldRevoke = true;
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = file.name || "arquivo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      if (shouldRevoke) window.URL.revokeObjectURL(href);
      return true;
    } catch {
      return false;
    }
  };

  const handleDownloadSelectedCustomItems = async (key) => {
    const selected = getSelectedCustomItems(key);
    const files = [
      ...selected.files,
      ...selected.folders.flatMap((folder) => collectCustomFolderFiles(key, folder)),
    ].filter((file, index, list) => file && list.findIndex((item) => item.id === file.id) === index);

    if (!files.length) {
      alert("Selecione arquivos ou pastas com arquivos para baixar.");
      return;
    }

    let downloaded = 0;
    for (const file of files) {
      // Sequential downloads avoid browser popup/download blocking in batch operations.
      // eslint-disable-next-line no-await-in-loop
      if (await downloadCustomFile(file)) downloaded += 1;
    }
    if (downloaded === 0) alert("Não foi possível baixar os itens selecionados.");
  };

  const handleDeleteSelectedCustomItems = async (key) => {
    const selected = getSelectedCustomItems(key);
    const total = selected.folders.length + selected.files.length;
    if (!total) return;
    const confirmed = window.confirm(`Tem certeza que deseja mover ${total} item${total === 1 ? "" : "s"} selecionado${total === 1 ? "" : "s"} para a lixeira?`);
    if (!confirmed) return;

    try {
      for (const file of selected.files) {
        // eslint-disable-next-line no-await-in-loop
        await moveCustomFileToSystemTrash(file, key);
      }

      const deletedAt = new Date().toISOString();
      const selectedFolderIds = new Set(selected.folders.map((folder) => folder.id));
      const selectedFileIds = new Set(selected.files.map((file) => file.id));
      const descendantKeys = selected.folders.flatMap((folder) => collectCustomFolderDescendantKeys(key, folder));
      const folderSnapshots = selected.folders.map((folder) => ({
        ...folder,
        originalKey: key,
        deletedAt,
        foldersByKey: Object.fromEntries(
          collectCustomFolderDescendantKeys(key, folder).map((folderKey) => [folderKey, customFolders[folderKey] || []]),
        ),
        filesByKey: Object.fromEntries(
          collectCustomFolderDescendantKeys(key, folder).map((folderKey) => [folderKey, customFolderFiles[folderKey] || []]),
        ),
      }));

      const nextFolders = Object.fromEntries(
        Object.entries({
          ...customFolders,
          [key]: (customFolders[key] || []).filter((folder) => !selectedFolderIds.has(folder.id)),
        }).filter(([folderKey]) => !descendantKeys.includes(folderKey)),
      );
      const nextFiles = Object.fromEntries(
        Object.entries({
          ...customFolderFiles,
          [key]: (customFolderFiles[key] || []).filter((file) => !selectedFileIds.has(file.id)),
        }).filter(([folderKey]) => !descendantKeys.includes(folderKey)),
      );

      setCustomFolders(nextFolders);
      setCustomFolderFiles(nextFiles);
      await persistFileManagerState(nextFolders, nextFiles);
      if (folderSnapshots.length) setCustomFolderTrash((prev) => [...folderSnapshots, ...prev]);
      await refreshStorageUsage();
      registerFileOperation({ action: "trash-selection", key, count: total });
      clearCustomSelection(key);
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível mover os itens selecionados para a lixeira.");
    }
  };

  const toggleAllCustomItems = (key) => {
    const allIds = [
      ...(customFolders[key] || []).map((folder) => customSelectionId("folder", folder.id)),
      ...(customFolderFiles[key] || []).map((file) => customSelectionId("file", file.id)),
    ];
    setSelectedCustomItems((prev) => {
      const current = prev[key] || [];
      const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
      return { ...prev, [key]: allSelected ? [] : allIds };
    });
  };

  const renderCustomSelectionToolbar = (key) => {
    const folderCount = (customFolders[key] || []).length;
    const fileCount = (customFolderFiles[key] || []).length;
    const selectedCount = getSelectedCustomIds(key).length;
    if (folderCount + fileCount === 0) return null;

    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-xs text-slate-600">
        <label className="inline-flex items-center gap-2 font-semibold">
          <input
            type="checkbox"
            checked={selectedCount > 0 && selectedCount === folderCount + fileCount}
            onChange={() => toggleAllCustomItems(key)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Selecionar todos
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <span>{selectedCount} selecionado{selectedCount === 1 ? "" : "s"}</span>
          <button type="button" disabled={!selectedCount} onClick={() => handleDownloadSelectedCustomItems(key)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"><FaDownload />Baixar</button>
          {canManageAccess && <button type="button" disabled={!selectedCount} onClick={() => handleDeleteSelectedCustomItems(key)} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 font-semibold text-red-500 transition hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"><FaTrashAlt />Excluir</button>}
        </div>
      </div>
    );
  };

  const renderCustomFolderRow = (key, folder) => {
    const childKey = getCustomFolderChildKey(key, folder);
    const itemCount = (customFolders[childKey] || []).length + (customFolderFiles[childKey] || []).length;

    return (
      <div key={folder.id} className="grid gap-3 px-4 py-4 text-left transition hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
        <span className="flex min-w-0 items-center gap-3">
          <input
            type="checkbox"
            checked={isCustomItemSelected(key, "folder", folder.id)}
            onChange={() => toggleCustomItemSelection(key, "folder", folder.id)}
            className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            aria-label={`Selecionar pasta ${folder.name}`}
          />
          <button type="button" onClick={() => openCustomFolder(key, folder)} className="flex min-w-0 items-center gap-3 text-left text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-200">
            <FaFolder className="shrink-0 text-xl text-amber-400" />
            <span className="truncate text-base font-semibold text-slate-700" title={folder.name}>{folder.name}</span>
          </button>
        </span>
        <span className="justify-self-center text-center text-xs text-slate-500">Pasta</span>
        <span className="justify-self-center text-center text-xs text-slate-500">{itemCount} {itemCount === 1 ? "item" : "itens"}</span>
        <span className="flex flex-wrap items-center gap-1">
          {canManageAccess && <button type="button" onClick={() => handleRenameCustomFolder(key, folder)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaEdit />Renomear</button>}
          {canManageAccess && <button type="button" onClick={() => handleCopyCustomFolder(key, folder)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaCopy />Copiar</button>}
          {canManageAccess && <button type="button" onClick={() => handleDeleteCustomFolder(key, folder)} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2 py-1 text-[10px] font-semibold text-red-500 transition hover:border-red-300 hover:bg-red-50"><FaTrashAlt />Excluir</button>}
        </span>
      </div>
    );
  };

  const renderProcessCard = (form) => {
    const folders = form.items
      .slice()
      .sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR"));
    const totalFields = folders.length;
    const restrictedCount = folders.filter(
      (folder) =>
        (folder.allowedUsers && folder.allowedUsers.length > 0) ||
        (folder.allowedSectors && folder.allowedSectors.length > 0),
    ).length;
    const hasFolders = totalFields > 0;

    const restrictionMessage = !hasFolders
      ? "Nenhum campo disponível neste processo."
      : restrictedCount > 0
      ? `${restrictedCount} ${restrictedCount === 1 ? "campo com restrição" : "campos com restrições"} de acesso.`
      : "";

    const handleOpenProcess = () => {
      if (!hasFolders) return;
      openProcessViewer(form);
    };

    const handleKeyDown = (event) => {
      if (!hasFolders) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openProcessViewer(form);
      }
    };

    return (
      <div
        role="button"
        tabIndex={hasFolders ? 0 : -1}
        onClick={handleOpenProcess}
        onKeyDown={handleKeyDown}
        aria-disabled={!hasFolders}
        className={`overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
          hasFolders ? "cursor-pointer hover:border-blue-300" : "cursor-not-allowed opacity-75"
        }`}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_150px_110px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
          <span>Nome</span>
          <span className="justify-self-center text-center">Tipo</span>
          <span className="justify-self-center text-center">Tamanho</span>
        </div>
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_150px_110px] sm:items-center">
          <div className="flex min-w-0 items-center gap-3">
            <FaFolder className="shrink-0 text-xl text-amber-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-700">Anexos vinculados</p>
              {restrictionMessage && <p className="truncate text-xs text-slate-500">{restrictionMessage}</p>}
            </div>
          </div>
          <span className="justify-self-center text-center text-xs text-slate-500">Pasta</span>
          <span className="justify-self-center text-center text-xs text-slate-500">
            {totalFields} {totalFields === 1 ? "campo" : "campos"}
          </span>
        </div>
      </div>
    );
  };

  const loadPreviewFile = ({ signal } = {}) => {
    if (!previewFile) return Promise.reject(new Error("Arquivo não encontrado."));
    if (previewFile.kind === "custom") {
      return getBlobForCustomFolderFile(previewFile.file, { signal });
    }
    if (previewFile.kind === "company-document") {
      return fetchExternalFileBlob(previewFile.document, { signal });
    }
    const file = previewFile.file;
    const directReference = resolveDirectFileReference(file);
    if (directReference) {
      return fetch(directReference, { signal }).then((response) => response.blob());
    }
    const requestPath = file.contentPath || resolveDownloadPath(getLegacyFileReference(file));
    return api.get(requestPath, { responseType: "blob", signal });
  };

  const downloadPreviewFile = () => {
    if (!previewFile) return;
    if (previewFile.kind === "custom") return handleDownloadCustomFolderFile(previewFile.file);
    if (previewFile.kind === "company-document") return handleDownloadExternalFile(previewFile.document);
    return handleDownloadFile(previewFile.file);
  };


  return (
    <div ref={containerRef} className="space-y-6">
      <header className="rounded-3xl bg-[#061136] p-6 text-white shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-2xl text-blue-100">
              <FaFileArchive />
            </span>
            <h1 className="text-2xl font-semibold">Gerenciador de Arquivos</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <TranslationSafeIconButton
              type="button"
              onClick={goToFileManagerHome}
              icon={FaHome}
              label="Início"
              className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur transition hover:bg-white/30"
            />
            <TranslationSafeIconButton
              type="button"
              onClick={loadData}
              icon={FaSyncAlt}
              label="Atualizar"
              className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur transition hover:bg-white/30"
            />
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-lg lg:max-w-2xl">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filterTerm}
              onChange={(event) => setFilterTerm(event.target.value)}
              placeholder="Buscar por pasta, categoria, processo, campo ou documento"
              className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}


      {filterTerm.trim().length >= 2 && (
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-700">Resultados em documentos</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
              {documentSearchLoading ? "Buscando..." : `${documentSearchResults.length} resultado${documentSearchResults.length === 1 ? "" : "s"}`}
            </span>
          </div>
          {documentSearchResults.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {documentSearchResults.slice(0, 25).map(({ file, folder }) => (
                <div key={`${folder.id}-${file.relativePath || file.name}`} className="grid gap-3 px-4 py-3 text-left sm:grid-cols-[minmax(0,1fr)_180px_170px] sm:items-center">
                  <span className="flex min-w-0 items-center gap-3 text-slate-600">
                    <FaFileAlt className="shrink-0 text-lg text-blue-500" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold" title={file.name}>{file.name}</span>
                      <span className="block truncate text-xs text-slate-500" title={`${folder.categoryName} › ${folder.formName} › ${folder.fieldName}`}>{folder.categoryName} › {folder.formName} › {folder.fieldName}</span>
                    </span>
                  </span>
                  <span className="text-xs text-slate-500">{formatBytes(file.size)}</span>
                  <span className="flex flex-wrap items-center gap-1">
                    <button type="button" onClick={() => handleViewFile(file)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Visualizar</button>
                    <button type="button" onClick={() => handleDownloadFile(file)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Baixar</button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              {documentSearchLoading ? "Procurando documentos nas pastas..." : "Nenhum documento encontrado com esse termo."}
            </div>
          )}
        </div>
      )}

      {auxiliaryLoading && !loading && (
        <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/80 px-4 py-3 shadow-sm" role="status" aria-live="polite">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold text-blue-800">
            <span>{loadProgress.label}</span>
            <span>{Math.round((loadProgress.completed / Math.max(loadProgress.total, 1)) * 100)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-blue-100" role="progressbar" aria-valuemin={0} aria-valuemax={loadProgress.total} aria-valuenow={loadProgress.completed}>
            <div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{ width: `${Math.round((loadProgress.completed / Math.max(loadProgress.total, 1)) * 100)}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-blue-700">O gerenciador já pode ser usado enquanto as bibliotecas complementares terminam de carregar.</p>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 p-8 text-slate-500 shadow-sm">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
            <FaSitemap className="animate-spin text-2xl text-blue-500" />
            <div>
              <p className="text-sm font-semibold text-slate-700">Carregando gerenciador de arquivos...</p>
              <p className="mt-1 text-xs text-slate-500">{loadProgress.label}</p>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={loadProgress.total} aria-valuenow={loadProgress.completed} aria-label="Progresso de carregamento do gerenciador de arquivos">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: `${Math.round((loadProgress.completed / Math.max(loadProgress.total, 1)) * 100)}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-500">{Math.round((loadProgress.completed / Math.max(loadProgress.total, 1)) * 100)}%</span>
          </div>
        </div>
) : (
        <section className="space-y-6">
          {activeCustomFolder ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div>
                  <h2 className="text-lg font-semibold text-slate-700">{activeCustomFolder.folder.name}</h2>
                  <p className="text-xs text-slate-500">Pasta personalizada</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={closeCustomFolder} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaArrowLeft />Voltar</button>
                  {canManageAccess && <button type="button" onClick={() => handleCreateCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"><FaPlus />Nova pasta</button>}
                  {canManageAccess && <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-600 transition hover:border-emerald-300 hover:bg-emerald-50">
                    <FaUpload />
                    Enviar arquivos
                    <input type="file" multiple className="sr-only" onChange={handleUploadCustomFolderFiles} />
                  </label>}
                  {canManageAccess && folderClipboard && (
                    <button type="button" onClick={() => handlePasteCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaPaste />Colar</button>
                  )}
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_150px_110px_220px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
                  <span>Nome</span><span className="justify-self-center text-center">Tipo</span><span className="justify-self-center text-center">Tamanho</span><span>Ações</span>
                </div>
                {renderCustomSelectionToolbar(getCurrentCustomFolderKey())}
                <div className="divide-y divide-slate-100">
                  {currentCustomFolders.map((folder) => renderCustomFolderRow(getCurrentCustomFolderKey(), folder))}
                  {[...(customFolderFiles[getCurrentCustomFolderKey()] || [])].sort(sortByName).map((file) => (
                    <div key={file.id} className="grid gap-3 px-4 py-3 text-left sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                      <span className="flex min-w-0 items-center gap-3 text-slate-600">
                        <input
                          type="checkbox"
                          checked={isCustomItemSelected(getCurrentCustomFolderKey(), "file", file.id)}
                          onChange={() => toggleCustomItemSelection(getCurrentCustomFolderKey(), "file", file.id)}
                          className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          aria-label={`Selecionar arquivo ${file.name}`}
                        />
                        <FaFileAlt className="shrink-0 text-lg text-blue-500" />
                        <span className="truncate text-sm font-semibold" title={file.name}>{file.name}</span>
                      </span>
                      <span className="justify-self-center text-center text-xs text-slate-500">{getSimpleFileType(file)}</span>
                      <span className="justify-self-center text-center text-xs text-slate-500">{formatBytes(file.size)}</span>
                      <span className="flex flex-wrap items-center gap-1">
                        {(getLegacyFileReference(file) || file.dataUrl) && <button type="button" onClick={() => handleViewCustomFolderFile(file)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaEye />Visualizar</button>}
                        {(getLegacyFileReference(file) || file.dataUrl) && <button type="button" onClick={() => handleDownloadCustomFolderFile(file)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaDownload />Baixar</button>}
                        {canManageAccess && <button type="button" onClick={() => handleRenameCustomFolderFile(file)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaEdit />Renomear</button>}
                        {canManageAccess && <button type="button" onClick={() => handleDeleteCustomFolderFile(file)} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2 py-1 text-[10px] font-semibold text-red-500 transition hover:border-red-300 hover:bg-red-50"><FaTrashAlt />Excluir</button>}
                      </span>
                    </div>
                  ))}
                  {(customFolders[getCurrentCustomFolderKey()] || []).length === 0 && (customFolderFiles[getCurrentCustomFolderKey()] || []).length === 0 && (
                    <div className="px-4 py-10 text-center text-sm text-slate-500">Envie arquivos ou crie subpastas para organizar esta pasta.</div>
                  )}
                </div>
              </div>
            </div>
          ) : !isProcessRootOpen && !activeAuxiliaryFolder ? (
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div className="min-w-0 flex-1 sm:min-w-[260px]">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-700">
                    <FaHdd className="text-blue-500" />
                    <span>Armazenamento do gerenciador</span>
                    <span className="text-xs font-medium text-slate-500">{formatBytes(usedStorageBytes)} de {formatBytes(storageLimitBytes)}</span>
                    {isMasterUser && (
                      <button type="button" onClick={handleChangeStorageLimit} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Alterar limite</button>
                    )}
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${storagePercent}%` }} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canManageAccess && <button type="button" onClick={() => handleCreateCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"><FaPlus />Nova pasta</button>}
                  {canManageAccess && folderClipboard && (
                    <button type="button" onClick={() => handlePasteCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaPaste />Colar</button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)_150px_110px_220px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
                <span>Nome</span>
                <span className="justify-self-center text-center">Tipo</span>
                <span className="justify-self-center text-center">Tamanho</span>
                <span>Ações</span>
              </div>
              {renderCustomSelectionToolbar("root")}
              {currentCustomFolders.map((folder) => renderCustomFolderRow("root", folder))}
              <button
                type="button"
                onClick={() => {
                  setIsProcessRootOpen(true);
                  resetCustomFolderNavigation();
                  setActiveAuxiliaryFolder("");
                  setSelectedCategorySlug("");
                  setSelectedFormSlug("");
                }}
                className="grid w-full gap-3 px-4 py-4 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <FaFolder className="shrink-0 text-xl text-amber-400" />
                  <span className="truncate text-base font-semibold text-slate-700">Arquivos de Processos</span>
                </span>
                <span className="justify-self-center text-center text-xs text-slate-500">Pasta</span>
                <span className="justify-self-center text-center text-xs text-slate-500">{filteredHierarchy.length} categorias</span>
                <span className="text-xs text-slate-400">Abrir</span>
              </button>
              {Object.entries(auxiliaryFolders).map(([key, folder]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setActiveAuxiliaryFolder(key);
                    resetCustomFolderNavigation();
                    setActiveStudentFolder(null);
                    setActiveEmployeeFolder(null);
                    setActiveFinanceFolder(null);
                    setActiveAssetFolder(null);
                    setIsProcessRootOpen(false);
                    setSelectedCategorySlug("");
                    setSelectedFormSlug("");
                  }}
                  className="grid w-full gap-3 px-4 py-4 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <FaFolder className="shrink-0 text-xl text-amber-400" />
                    <span className="truncate text-base font-semibold text-slate-700">{folder.title}</span>
                  </span>
                  <span className="justify-self-center text-center text-xs text-slate-500">{folder.type}</span>
                  <span className="justify-self-center text-center text-xs text-slate-500">{folder.size}</span>
                  <span className="text-xs text-slate-400">Abrir</span>
                </button>
              ))}
            </div>
          ) : activeAuxiliaryFolder ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-700">{auxiliaryFolders[activeAuxiliaryFolder]?.title}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => { setActiveAuxiliaryFolder(""); setActiveStudentFolder(null); setActiveEmployeeFolder(null); setActiveFinanceFolder(null); setActiveAssetFolder(null); }} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaArrowLeft />Voltar</button>
                  {canManageAccess && <button type="button" onClick={() => handleCreateCustomFolder(activeAuxiliaryFolder)} className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"><FaPlus />Nova pasta</button>}
                  {canManageAccess && folderClipboard && (
                    <button type="button" onClick={() => handlePasteCustomFolder(activeAuxiliaryFolder)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaPaste />Colar</button>
                  )}
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_150px_110px_220px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
                  <span>Nome</span><span className="justify-self-center text-center">Tipo</span><span className="justify-self-center text-center">Tamanho</span><span>Ações</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {(activeAuxiliaryFolder === "students" && activeStudentFolder) || (activeAuxiliaryFolder === "employees" && activeEmployeeFolder) || (activeAuxiliaryFolder === "finance" && activeFinanceFolder) || (activeAuxiliaryFolder === "assets" && activeAssetFolder) ? (
                    (() => {
                      const currentFolder = activeStudentFolder || activeEmployeeFolder || activeFinanceFolder || activeAssetFolder;
                      const backLabel = activeAuxiliaryFolder === "students" ? "Voltar aos alunos" : activeAuxiliaryFolder === "employees" ? "Voltar aos colaboradores" : activeAuxiliaryFolder === "finance" ? "Voltar ao financeiro" : "Voltar aos ativos";
                      const backAction = () => { setActiveStudentFolder(null); setActiveEmployeeFolder(null); setActiveFinanceFolder(null); setActiveAssetFolder(null); };
                      return (
                        <>
                          <div className="flex items-center justify-between gap-3 px-4 py-3">
                            <strong className="text-sm text-slate-700">{currentFolder.title}</strong>
                            <button type="button" onClick={backAction} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaArrowLeft />{backLabel}</button>
                          </div>
                          {activeAuxiliaryFolder === "students" && studentFolderLoading && (
                            <div className="px-4 py-4" role="status" aria-live="polite">
                              <div className="mb-2 flex items-center justify-between text-xs font-semibold text-blue-700"><span>Carregando documentos do aluno...</span><span>aguarde</span></div>
                              <div className="h-2 overflow-hidden rounded-full bg-blue-100"><div className="file-manager-indeterminate h-full w-2/5 rounded-full bg-blue-500" /></div>
                            </div>
                          )}
                          {activeAuxiliaryFolder === "students" && currentFolder.loadError && (
                            <div className="mx-4 my-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{currentFolder.loadError}</div>
                          )}
                          {(currentFolder.documents || []).map((doc) => (
                            <div key={doc.id || doc.name} className="grid gap-3 px-4 py-3 text-left sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                              <span className="flex min-w-0 items-center gap-3 text-slate-600">
                                <FaFileAlt className="shrink-0 text-lg text-blue-500" />
                                <span className="truncate text-sm font-semibold" title={doc.name}>{doc.name}</span>
                              </span>
                              <span className="justify-self-center text-center text-xs text-slate-500">{doc.category || doc.type || "Documento"}</span>
                              <span className="justify-self-center text-center text-xs text-slate-500">{doc.uploadedAt ? formatDateTime(doc.uploadedAt) : (doc.sourceDate || doc.description || "-")}</span>
                              <span className="flex flex-wrap items-center gap-1">
                                {(doc.dataUrl || doc.url || doc.contentPath || doc.downloadPath || doc.downloadUrl || doc.relativePath) && <button type="button" onClick={() => handleViewFile(doc)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Visualizar</button>}
                                {(doc.dataUrl || doc.url || doc.contentPath || doc.downloadPath || doc.downloadUrl || doc.relativePath) && <button type="button" onClick={() => handleDownloadFile(doc)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Baixar</button>}
                              </span>
                            </div>
                          ))}
                          {(currentFolder.documents || []).length === 0 && !(activeAuxiliaryFolder === "students" && studentFolderLoading) && <div className="px-4 py-10 text-center text-sm text-slate-500">{currentFolder.id === "forecast" ? "Existem previsões cadastradas, mas nenhuma possui arquivo anexado." : "Nenhum arquivo vinculado a esta pasta."}</div>}
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {renderCustomSelectionToolbar(activeAuxiliaryFolder)}
                      {[...(customFolders[activeAuxiliaryFolder] || [])].sort(sortByName).map((folder) => renderCustomFolderRow(activeAuxiliaryFolder, folder))}
                      {activeAuxiliaryFolder === "registration" && companyDocuments.map((doc) => (
                        <div key={doc.id || doc.fileUrl || doc.name} className="grid gap-3 px-4 py-3 text-left sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                          <span className="flex min-w-0 items-center gap-3 text-slate-600">
                            <FaFileAlt className="shrink-0 text-lg text-blue-500" />
                            <span className="truncate text-sm font-semibold" title={doc.name}>{doc.name}</span>
                          </span>
                          <span className="justify-self-center text-center text-xs text-slate-500">Documento da empresa</span>
                          <span className="justify-self-center text-center text-xs text-slate-500">{getSimpleFileType(doc)}</span>
                          <span className="flex flex-wrap items-center gap-1">
                            {doc.contentPath && <button type="button" onClick={() => handleViewExternalFile(doc)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaEye />Visualizar</button>}
                            {doc.contentPath && <button type="button" onClick={() => handleDownloadExternalFile(doc)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"><FaDownload />Baixar</button>}
                            {!doc.contentPath && <span className="text-[10px] font-semibold text-amber-600">Documento sem identificador válido.</span>}
                          </span>
                        </div>
                      ))}
                      {activeAuxiliaryFolder === "students" && studentFileFolders.map((folder) => (
                        <button key={folder.id} type="button" onClick={() => openStudentFileFolder(folder)} className="grid w-full gap-3 px-4 py-3 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                          <span className="flex min-w-0 items-center gap-3">
                            <FaFolder className="shrink-0 text-xl text-amber-400" />
                            <span className="truncate text-sm font-semibold" title={folder.title}>{folder.title}</span>
                          </span>
                          <span className="justify-self-center text-center text-xs text-slate-500">Aluno</span>
                          <span className="justify-self-center text-center text-xs text-slate-500">{Array.isArray(folder.documents) ? `${folder.documents.length} ${folder.documents.length === 1 ? "arquivo" : "arquivos"}` : "Carregar ao abrir"}</span>
                          <span className="text-xs text-slate-400">Abrir</span>
                        </button>
                      ))}
                      {activeAuxiliaryFolder === "employees" && employeeFileFolders.map((folder) => (
                        <button key={folder.id} type="button" onClick={() => setActiveEmployeeFolder(folder)} className="grid w-full gap-3 px-4 py-3 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                          <span className="flex min-w-0 items-center gap-3"><FaFolder className="shrink-0 text-xl text-amber-400" /><span className="truncate text-sm font-semibold" title={folder.title}>{folder.title}</span></span>
                          <span className="justify-self-center text-center text-xs text-slate-500">Colaborador</span>
                          <span className="justify-self-center text-center text-xs text-slate-500">{folder.documents.length} {folder.documents.length === 1 ? "arquivo" : "arquivos"}</span>
                          <span className="text-xs text-slate-400">Abrir</span>
                        </button>
                      ))}
                      {activeAuxiliaryFolder === "finance" && financeFileFolders.map((folder) => (
                        <button key={folder.id} type="button" onClick={() => setActiveFinanceFolder(folder)} className="grid w-full gap-3 px-4 py-3 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                          <span className="flex min-w-0 items-center gap-3"><FaFolder className="shrink-0 text-xl text-amber-400" /><span className="truncate text-sm font-semibold" title={folder.title}>{folder.title}</span></span>
                          <span className="justify-self-center text-center text-xs text-slate-500">Pasta</span>
                          <span className="justify-self-center text-center text-xs text-slate-500">{folder.documents.length} {folder.documents.length === 1 ? "item" : "itens"}</span>
                          <span className="text-xs text-slate-400">Abrir</span>
                        </button>
                      ))}
                      {activeAuxiliaryFolder === "assets" && assetFileFolders.map((folder) => (
                        <button key={folder.id} type="button" onClick={() => setActiveAssetFolder(folder)} className="grid w-full gap-3 px-4 py-3 text-left text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                          <span className="flex min-w-0 items-center gap-3"><FaFolder className="shrink-0 text-xl text-amber-400" /><span className="truncate text-sm font-semibold" title={folder.title}>{folder.title}</span></span>
                          <span className="justify-self-center text-center text-xs text-slate-500">Ativo</span>
                          <span className="justify-self-center text-center text-xs text-slate-500">{folder.documents.length} {folder.documents.length === 1 ? "arquivo" : "arquivos"}</span>
                          <span className="text-xs text-slate-400">Abrir</span>
                        </button>
                      ))}
                      {(customFolders[activeAuxiliaryFolder] || []).length === 0 && (activeAuxiliaryFolder !== "students" || studentFileFolders.length === 0) && (activeAuxiliaryFolder !== "employees" || employeeFileFolders.length === 0) && (activeAuxiliaryFolder !== "finance" || financeFileFolders.length === 0) && (activeAuxiliaryFolder !== "assets" || assetFileFolders.length === 0) && (activeAuxiliaryFolder !== "registration" || companyDocuments.length === 0) && (
                        <div className="px-4 py-10 text-center text-sm text-slate-500">Crie pastas para organizar estes arquivos.</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : activeCategory ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div>
                  <h2 className="text-lg font-semibold text-slate-700">{activeCategory.category}</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategorySlug("");
                      setSelectedFormSlug("");
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                  >
                    Voltar
                  </button>
                  {canManageAccess && <button
                    type="button"
                    onClick={() => handleCreateCustomFolder(`category:${activeCategory.categorySlug}`)}
                    className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    <FaPlus />
                    Nova pasta
                  </button>}
                  {canManageAccess && folderClipboard && (
                    <button
                      type="button"
                      onClick={() => handlePasteCustomFolder(`category:${activeCategory.categorySlug}`)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                    >
                      <FaPaste />
                      Colar
                    </button>
                  )}
                </div>
              </div>

              {activeCategory.forms.length > 0 ? (
                <>
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FaSitemap className="text-blue-500" />
                        <h3 className="text-base font-semibold text-slate-600">Processos</h3>
                      </div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                        {activeCategory.forms.length} {activeCategory.forms.length === 1 ? "processo" : "processos"}
                      </span>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_150px_110px_220px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
                      <span>Nome</span>
                      <span className="justify-self-center text-center">Tipo</span>
                      <span className="justify-self-center text-center">Tamanho</span>
                      <span>Ações</span>
                    </div>
                    {renderCustomSelectionToolbar(`category:${activeCategory.categorySlug}`)}
                    <div className="divide-y divide-slate-100">
                      {[...(customFolders[`category:${activeCategory.categorySlug}`] || [])].sort(sortByName).map((folder) => renderCustomFolderRow(`category:${activeCategory.categorySlug}`, folder))}
                      {activeCategory.forms.map((form) => {
                        const isProcessActive = form.formSlug === selectedFormSlug;
                        return (
                          <div
                            key={`${activeCategory.categorySlug}-${form.formSlug}`}
                            className={`grid gap-3 px-4 py-3 text-left transition sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center ${
                              isProcessActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            <button type="button" onClick={() => setSelectedFormSlug(form.formSlug)} className="flex min-w-0 items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-200">
                              <FaFolder className="shrink-0 text-xl text-amber-400" />
                              <span className="truncate text-sm font-semibold" title={form.formName}>{form.formName}</span>
                            </button>
                            <span className="justify-self-center text-center text-xs text-slate-500">Pasta</span>
                            <span className="justify-self-center text-center text-xs text-slate-500">{form.items.length} {form.items.length === 1 ? "campo" : "campos"}</span>
                            <span className="flex flex-wrap items-center gap-1">
                              {canManageAccess && (
                                <>
                                  <button type="button" onClick={() => openFormRename(activeCategory, form)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Renomear</button>
                                  <button type="button" onClick={() => openFormMerge(activeCategory, form)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Mesclar</button>
                                </>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {activeProcess ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                        <h3 className="text-lg font-semibold text-slate-700">{activeProcess.formName}</h3>
                        <div className="ml-auto flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => setSelectedFormSlug("")} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaArrowLeft />Voltar</button>
                          {canManageAccess && <button type="button" onClick={() => handleCreateCustomFolder(`process:${activeCategory.categorySlug}:${activeProcess.formSlug}`)} className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"><FaPlus />Nova pasta</button>}
                          {canManageAccess && folderClipboard && (
                            <button type="button" onClick={() => handlePasteCustomFolder(`process:${activeCategory.categorySlug}:${activeProcess.formSlug}`)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaPaste />Colar</button>
                          )}
                          {canManageAccess && (
                            <button type="button" onClick={() => openProcessViewer(activeProcess, { manageAccess: true })} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600">Gerenciar acesso</button>
                          )}
                        </div>
                      </div>
                      {currentCustomFolders.length > 0 && (
                        <div className="max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                          {renderCustomSelectionToolbar(getCurrentCustomFolderKey())}
                          <div className="divide-y divide-slate-100">
                            {currentCustomFolders.map((folder) => renderCustomFolderRow(getCurrentCustomFolderKey(), folder))}
                          </div>
                        </div>
                      )}
                      {activeProcess.items.length > 0 ? (
                        <div className="max-w-4xl">{renderProcessCard(activeProcess)}</div>
                      ) : (
                        <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                          Nenhum campo disponível para este processo.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                      Selecione um processo para visualizar as pastas disponíveis.
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                  Nenhum processo cadastrado nesta categoria.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-700">Arquivos de Processos</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setIsProcessRootOpen(false)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaArrowLeft />Voltar</button>
                  {canManageAccess && <button type="button" onClick={() => handleCreateCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"><FaPlus />Nova pasta</button>}
                  {canManageAccess && folderClipboard && (
                    <button type="button" onClick={() => handlePasteCustomFolder(getCurrentCustomFolderKey())} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"><FaPaste />Colar</button>
                  )}
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_150px_110px_220px] border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-500 max-sm:hidden">
                  <span>Nome</span>
                  <span className="justify-self-center text-center">Tipo</span>
                  <span className="justify-self-center text-center">Tamanho</span>
                  <span>Ações</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {currentCustomFolders.map((folder) => renderCustomFolderRow(getCurrentCustomFolderKey(), folder))}
                  {filteredHierarchy.map((category) => (
                    <div key={category.categorySlug} className="grid gap-3 px-4 py-3 text-left text-slate-600 transition hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_150px_110px_220px] sm:items-center">
                      <button type="button" onClick={() => { setSelectedCategorySlug(category.categorySlug); setSelectedFormSlug(""); }} className="flex min-w-0 items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-200">
                        <FaFolder className="shrink-0 text-xl text-amber-400" />
                        <span className="truncate text-sm font-semibold" title={category.category}>{category.category}</span>
                      </button>
                      <span className="justify-self-center text-center text-xs text-slate-500">Categoria</span>
                      <span className="justify-self-center text-center text-xs text-slate-500">{category.forms.length} {category.forms.length === 1 ? "processo" : "processos"}</span>
                      <span className="flex flex-wrap items-center gap-1">
                        {canManageAccess && (
                          <>
                            <button type="button" onClick={() => openCategoryRename(category)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Renomear</button>
                            <button type="button" onClick={() => openCategoryMerge(category)} className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600">Mesclar</button>
                            <button type="button" onClick={() => handleDeleteCategory(category)} className="rounded-full border border-red-200 px-2 py-1 text-[10px] font-semibold text-red-500 transition hover:border-red-300 hover:bg-red-50">Excluir</button>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {duplicateUpload && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Arquivo duplicado</h2>
              <p className="mt-2 text-sm text-slate-500">
                Já existe um arquivo chamado <strong className="text-slate-700">{duplicateUpload.fileName}</strong> nesta pasta. O que deseja fazer?
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <button type="button" onClick={() => resolveDuplicateUploadAction("replace")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-100"><FaSyncAlt />Substituir</button>
              <button type="button" onClick={() => resolveDuplicateUploadAction("rename")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"><FaEdit />Renomear</button>
              <button type="button" onClick={() => resolveDuplicateUploadAction("cancel")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"><FaTimes />Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-800">Gerenciar acesso</h2>
              <p className="text-sm text-slate-500">Defina quem pode baixar arquivos desta pasta.</p>
              <p className="mt-3 text-sm font-medium text-slate-700">{editing.title}</p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Usuários autorizados</h3>
                    <p className="text-xs text-slate-500">Selecione os usuários que poderão baixar os arquivos.</p>
                  </div>
                  {filteredUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleAllEditingSelections("userIds", filteredUserIds)}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                      {filteredUserIds.every((value) => editing.userIds.includes(value))
                        ? "Limpar seleção"
                        : "Selecionar visíveis"}
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      placeholder="Filtrar por nome ou e-mail"
                      className="w-full rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-600 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="mt-4 flex-1">
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((userOption) => {
                        const value = String(userOption.id);
                        const checked = editing.userIds.includes(value);
                        return (
                          <label
                            key={userOption.id}
                            className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:border-blue-200"
                          >
                            <span className="flex-1">
                              <span className="block font-medium text-slate-700">{userOption.name}</span>
                              <span className="block text-xs text-slate-500">{userOption.email}</span>
                            </span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEditingSelection("userIds", value)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                        Nenhum usuário encontrado.
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-400">
                  Se nenhum usuário for selecionado, todos os usuários com login poderão acessar.
                </p>
              </div>
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Setores autorizados</h3>
                    <p className="text-xs text-slate-500">Limite o acesso por setores da organização.</p>
                  </div>
                  {filteredSectors.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleAllEditingSelections("sectorIds", filteredSectorIds)}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                      {filteredSectorIds.every((value) => editing.sectorIds.includes(value))
                        ? "Limpar seleção"
                        : "Selecionar visíveis"}
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={sectorSearch}
                      onChange={(event) => setSectorSearch(event.target.value)}
                      placeholder="Filtrar por nome do setor"
                      className="w-full rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-600 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="mt-4 flex-1">
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {filteredSectors.length > 0 ? (
                      filteredSectors.map((sectorOption) => {
                        const value = String(sectorOption.id);
                        const checked = editing.sectorIds.includes(value);
                        return (
                          <label
                            key={sectorOption.id}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:border-blue-200"
                          >
                            <span className="flex-1 font-medium text-slate-700">{sectorOption.name}</span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEditingSelection("sectorIds", value)}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                        Nenhum setor encontrado.
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-400">
                  Combine restrições de usuários e setores para personalizar o acesso.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveAccess}
                disabled={saving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  saving ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {saving ? "Salvando..." : "Salvar acesso"}
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryEditor && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800">Renomear categoria</h2>
            <p className="mt-2 text-sm text-slate-500">Atualize o nome exibido para os processos desta categoria.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={categoryEditor.name}
                onChange={(event) =>
                  setCategoryEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome da categoria"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCategoryEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitCategoryRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryMerge && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800">Mesclar categorias</h2>
            <p className="mt-2 text-sm text-slate-500">
              Escolha a categoria de destino para unificar todos os processos e campos.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500">Categoria de destino</label>
                <select
                  value={categoryMerge.targetSlug}
                  onChange={(event) =>
                    setCategoryMerge((prev) =>
                      prev
                        ? {
                            ...prev,
                            targetSlug: event.target.value,
                            name:
                              categoryOptions.find((option) => option.slug === event.target.value)?.name ??
                              prev.name,
                          }
                        : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Selecione uma categoria</option>
                  {categoryOptions
                    .filter((option) => option.slug !== categoryMerge.slug)
                    .map((option) => (
                      <option key={option.slug} value={option.slug}>
                        {option.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500">Nome após a mesclagem</label>
                <input
                  type="text"
                  value={categoryMerge.name}
                  onChange={(event) =>
                    setCategoryMerge((prev) =>
                      prev ? { ...prev, name: event.target.value } : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome final da categoria"
                />
              </div>
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCategoryMerge}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitCategoryMerge}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Mesclando..." : "Mesclar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {formEditor && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800">Renomear processo</h2>
            <p className="mt-2 text-sm text-slate-500">Defina um novo nome para todos os campos deste processo.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={formEditor.name}
                onChange={(event) =>
                  setFormEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome do processo"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFormEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFormRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {formMerge && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800">Mesclar processos</h2>
            <p className="mt-2 text-sm text-slate-500">
              Escolha o processo que receberá os campos e defina o nome final após a unificação.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500">Processo de destino</label>
                <select
                  value={formMerge.targetSlug}
                  onChange={(event) =>
                    setFormMerge((prev) =>
                      prev
                        ? {
                            ...prev,
                            targetSlug: event.target.value,
                            name:
                              formMergeOptions.find((option) => option.formSlug === event.target.value)?.formName ??
                              prev.name,
                          }
                        : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Selecione um processo</option>
                  {formMergeOptions
                    .filter((option) => option.formSlug !== formMerge.formSlug)
                    .map((option) => (
                      <option key={option.formSlug} value={option.formSlug}>
                        {option.formName}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500">Nome após a mesclagem</label>
                <input
                  type="text"
                  value={formMerge.name}
                  onChange={(event) =>
                    setFormMerge((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome final do processo"
                />
              </div>
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFormMerge}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFormMerge}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Mesclando..." : "Mesclar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fieldEditor && (
        <div className="fixed inset-0 z-[100000] flex min-h-screen items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800">Renomear campo</h2>
            <p className="mt-2 text-sm text-slate-500">Altere o nome do campo sem perder os anexos já catalogados.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={fieldEditor.name}
                onChange={(event) =>
                  setFieldEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome do campo"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFieldEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFieldRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <FilePreviewModal
        isOpen={Boolean(previewFile)}
        fileName={previewFile?.name || "arquivo"}
        loadFile={loadPreviewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={downloadPreviewFile}
      />

      {renamingFile && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-800">Alterar nome do arquivo</h3>
            <p className="mt-1 text-sm text-slate-500">Apenas o nome exibido será atualizado; o arquivo físico permanece no mesmo local.</p>
            <input
              type="text"
              value={fileRenameValue}
              onChange={(event) => setFileRenameValue(event.target.value)}
              className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              autoFocus
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={closeFileRename} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button type="button" onClick={handleRenameFile} disabled={renamingKey === renamingFile.relativePath} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${renamingKey === renamingFile.relativePath ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"}`}>{renamingKey === renamingFile.relativePath ? "Salvando..." : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {activeFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
          <div className="flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {activeFolder.categoryName}
                    </span>
                    <h2 className="text-xl font-semibold text-slate-800">{activeFolder.formName}</h2>
                    <p className="text-sm text-slate-500">Anexos organizados por solicitação e campo.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canManageAccess && (
                      <>
                        <button
                          type="button"
                          onClick={() => openFieldRename(activeFolder)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                        >
                          Renomear campo
                        </button>
                        <TranslationSafeIconButton
                          type="button"
                          onClick={() => openAccessModal(activeFolder)}
                          icon={FaLock}
                          label="Gerenciar acesso do campo"
                          className="inline-flex items-center gap-2 rounded-full border border-blue-500 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                        />
                      </>
                    )}
                    <button
                      type="button"
                      onClick={closeViewer}
                      className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-100"
                    >
                    <FaTimes />
                  </button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-600">Campo selecionado:</span>
                  {viewerFolders.length > 1 ? (
                    <select
                      value={String(activeFolder.id)}
                      onChange={(event) => changeViewerFolder(event.target.value)}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      {viewerFolders.map((folderOption) => (
                        <option key={folderOption.id} value={String(folderOption.id)}>
                          {folderOption.fieldName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {activeFolder.fieldName}
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-400">
                  {viewerFolders.length} {viewerFolders.length === 1 ? "campo neste processo" : "campos neste processo"}
                </span>
              </div>
            </div>
            <div className="grid gap-4 border-b border-slate-100 p-6 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={fileSearch}
                  onChange={(event) => setFileSearch(event.target.value)}
                  placeholder="Buscar por nome, protocolo ou solicitante"
                  className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <select
                value={yearFilter}
                onChange={(event) => setYearFilter(event.target.value)}
                className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Todos os anos</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                disabled={!yearFilter}
                className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="">Todos os meses</option>
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between px-6 pb-4 text-xs text-slate-500">
              <span>Total catalogado: {folderSummary?.totalFiles ?? 0}</span>
              <span>Exibindo: {folderSummary?.totalMatching ?? folderFiles.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-6 pt-0">
              {filesError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {filesError}
                </div>
              ) : filesLoading ? (
                <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white py-12 text-slate-500">
                  <FaSitemap className="mr-2 animate-spin" /> Carregando anexos...
                </div>
              ) : shouldSeparateByFieldFolders ? (
                <div className="space-y-3">
                  {fieldFolderGroups.map((field) => {
                    const key = String(field.folder.id);
                    const isExpanded = expandedGroups[key] ?? false;
                    return (
                      <div key={key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <button
                          type="button"
                          onClick={() => toggleGroupExpansion(key)}
                          className="flex w-full items-center justify-between gap-3 bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <FaFolder className={`shrink-0 text-lg ${isExpanded ? "text-blue-500" : "text-amber-400"}`} />
                            <div className="min-w-0 space-y-1">
                              <p className="truncate text-sm font-semibold text-slate-700" title={field.folder.fieldName}>{field.folder.fieldName}</p>
                              <p className="truncate text-xs text-slate-500">{field.folder.categoryName} › {field.folder.formName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>Total: {field.totalFiles}</span>
                            <span>Exibidos: {field.totalMatching}</span>
                            <span className={`text-lg font-semibold text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}>▾</span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="divide-y divide-slate-100">
                            {field.files.length > 0 ? (
                              field.files.map((file) => (
                                <div key={file.relativePath} className="flex flex-col gap-3 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-center gap-3">
                                    <FaFileAlt className="text-slate-400" />
                                    <div className="flex min-w-0 flex-col">
                                      <span className="truncate font-medium text-slate-700" title={file.name}>{file.name}</span>
                                      <div className="flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                                        <span>{formatBytes(file.size)}</span>
                                        <span>{file.monthName ? `${file.monthName} / ${file.year}` : file.year}</span>
                                        <span>{formatDateTime(file.modifiedAt)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-end gap-2">
                                    <button type="button" onClick={() => handleViewFile(file)} title="Visualizar arquivo" aria-label="Visualizar arquivo" className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600"><FaEye /></button>
                                    <button type="button" onClick={() => handleDownloadFile(file)} disabled={downloadingKey === file.relativePath} title={downloadingKey === file.relativePath ? "Baixando..." : "Baixar arquivo"} aria-label="Baixar arquivo" className={`rounded-full border border-slate-200 p-2 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 ${downloadingKey === file.relativePath ? "cursor-not-allowed text-blue-300" : "text-slate-500"}`}><FaDownload /></button>
                                    {canManageAccess && (
                                      <>
                                        <button type="button" onClick={() => openFileRename(file)} title="Alterar nome" aria-label="Alterar nome" className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600"><FaEdit /></button>
                                        <button type="button" onClick={() => handleDeleteFile(file)} disabled={deletingKey === file.relativePath} title={deletingKey === file.relativePath ? "Excluindo..." : "Excluir arquivo"} aria-label="Excluir arquivo" className={`rounded-full border border-slate-200 p-2 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 ${deletingKey === file.relativePath ? "cursor-not-allowed text-red-300" : "text-slate-500"}`}><FaTrashAlt /></button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="px-4 py-4 text-xs text-slate-400">Nenhum anexo corresponde aos filtros informados para esta pasta.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : folderSummary?.totalFiles === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500">
                  Nenhum anexo foi salvo nesta pasta ainda.
                </div>
              ) : folderFiles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500">
                  Nenhum anexo corresponde aos filtros informados.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Arquivo</th>
                        <th className="px-4 py-3 text-left font-semibold">Solicitação</th>
                        <th className="px-4 py-3 text-left font-semibold">Período</th>
                        <th className="px-4 py-3 text-left font-semibold">Atualizado</th>
                        <th className="px-4 py-3 text-right font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {folderFiles.map((file) => (
                        <tr key={file.relativePath} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <FaFileAlt className="text-slate-400" />
                              <div className="flex min-w-0 flex-col">
                                <span className="truncate font-medium text-slate-700" title={file.name}>
                                  {file.name}
                                </span>
                                <span className="text-xs text-slate-400">{formatBytes(file.size)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {file.request ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-700">{file.request.label}</span>
                                <span className="text-xs text-slate-400">{file.request.sector || file.request.requester ? [file.request.requester, file.request.sector].filter(Boolean).join(" • ") : ""}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">Sem identificação da solicitação</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {file.monthName ? `${file.monthName} / ${file.year}` : file.year}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDateTime(file.modifiedAt)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button type="button" onClick={() => handleViewFile(file)} title="Visualizar arquivo" aria-label="Visualizar arquivo" className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600"><FaEye /></button>
                              <button type="button" onClick={() => handleDownloadFile(file)} disabled={downloadingKey === file.relativePath} title={downloadingKey === file.relativePath ? "Baixando..." : "Baixar arquivo"} aria-label="Baixar arquivo" className={`rounded-full border border-slate-200 p-2 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 ${downloadingKey === file.relativePath ? "cursor-not-allowed text-blue-300" : "text-slate-500"}`}><FaDownload /></button>
                              {canManageAccess && (
                                <>
                                  <button type="button" onClick={() => openFileRename(file)} title="Alterar nome" aria-label="Alterar nome" className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600"><FaEdit /></button>
                                  <button type="button" onClick={() => handleDeleteFile(file)} disabled={deletingKey === file.relativePath} title={deletingKey === file.relativePath ? "Excluindo..." : "Excluir arquivo"} aria-label="Excluir arquivo" className={`rounded-full border border-slate-200 p-2 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 ${deletingKey === file.relativePath ? "cursor-not-allowed text-red-300" : "text-slate-500"}`}><FaTrashAlt /></button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
